import React, { useState } from "react";
import { useI18n } from "@/i18n";
import { api, unwrap, errMsg } from "@/lib/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { chargeableTerms, chargedLabelsFromDues, chargedMonthlyMonths, chargedMonths, customLinePayload, displayPeriodLabel, dropChargedSelections, isOptionCharged, otherFeeCharged, overdueTermSelections, selectedMonthsPayload, submissionKey, termChargedStandard } from "@/lib/feeSelection";
import { OtherCustomPanel, TermCustomPanel, customEntryProblem } from "@/components/CustomFeeEntry";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { formatMoney, formatDate, statusTone, today } from "@/lib/utils";
import { levelDisplayName, levelWord, levelSystemOf } from "@/lib/levelLabels";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandList,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  FileText,
  Wallet,
  Plus,
  Pencil,
  Tag,
  RefreshCcw,
  Download,
  Calendar,
  AlertTriangle,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { CAPABILITY, hasCapability } from "@/lib/capabilityPolicy";
import { useLookupValues } from "@/lib/platformConfig";
import { BalanceTotals, CustomFeesReport, TermBalanceReport, monthLabel } from "@/components/FeePlan";

const tone = (t) =>
  ({
    success: "bg-[var(--success-bg)] text-[var(--success)]",
    warning: "bg-[var(--warning-bg)] text-[var(--warning)]",
    error: "bg-[var(--error-bg)] text-[var(--error)]",
    info: "bg-[var(--info-bg)] text-[var(--info)]",
  })[t] || "bg-[var(--subtle)] text-[var(--text-secondary)]";

function FieldLabel({ children, required = false, id }) {
  return (
    <Label id={id} className="text-xs uppercase">
      {children}
      {required && (
        <span className="ml-1 text-[var(--error)]" aria-hidden="true">
          *
        </span>
      )}
    </Label>
  );
}

const STUDENT_FEE_PAYMENT_ROLES = ["tenant_owner", "administrator", "principal", "accountant"];

export default function Fees() {
  const { user } = useAuth();
  const { t, direction } = useI18n();
  const roles = user?.roles || [];
  const canWrite = hasCapability(user, CAPABILITY.FINANCE_MANAGE);
  // Owner, Administrator, Principal and Accountant record student fee
  // payments and manage discounts with the same access (design plan R5.2).
  const isFeeStaff = roles.some((role) => STUDENT_FEE_PAYMENT_ROLES.includes(role));
  const canCreateStudentFeePayment = isFeeStaff && canWrite;

  return (
    <div
      className="min-w-0 max-w-[1600px] mx-auto p-4 sm:p-6 lg:p-8"
      data-testid="fees-page"
    >
      <div className="mb-6">
        <h1 className="font-display text-3xl font-bold text-[var(--text-primary)]">
          {t("finance.invoices")}
        </h1>
        <p className="text-sm text-[var(--text-muted)] mt-1">
          {t("finance.invoicesSubtitle")}
        </p>
      </div>

      <Tabs defaultValue="invoices" className="w-full min-w-0" dir={direction}>
        <TabsList className="w-full max-w-full justify-start overflow-x-auto">
          <TabsTrigger value="invoices" data-testid="tab-invoices">
            {t("finance.invoices")}
          </TabsTrigger>
          <TabsTrigger value="structures" data-testid="tab-structures">
            {t("finance.feeStructures")}
          </TabsTrigger>
          <TabsTrigger value="discounts" data-testid="tab-discounts">
            {t("finance.discounts")}
          </TabsTrigger>
          <TabsTrigger value="refunds" data-testid="tab-refunds">
            {t("finance.refunds")}
          </TabsTrigger>
          <TabsTrigger value="reports" data-testid="tab-reports">
            {t("finance.reports")}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="invoices">
          <InvoicesTab
            canWrite={canWrite}
            canCreateStudentFeePayment={canCreateStudentFeePayment}
          />
        </TabsContent>
        <TabsContent value="structures">
          <StructuresTab canWrite={canWrite} />
        </TabsContent>
        <TabsContent value="discounts">
          <DiscountsTab canWrite={canWrite} canManageCustomDiscounts={isFeeStaff && canWrite} />
        </TabsContent>
        <TabsContent value="refunds">
          <RefundsTab />
        </TabsContent>
        <TabsContent value="reports">
          <ReportsTab canWrite={canWrite} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function InvoicesTab({ canWrite, canCreateStudentFeePayment }) {
  const [status, setStatus] = useState("all");
  const [payOpen, setPayOpen] = useState(null);
  const [voidOpen, setVoidOpen] = useState(null);
  const [refundOpen, setRefundOpen] = useState(null);
  const [refundAndCancelOpen, setRefundAndCancelOpen] = useState(null);
  const [discountOpen, setDiscountOpen] = useState(null);
  const [genOpen, setGenOpen] = useState(false);
  const [feePayOpen, setFeePayOpen] = useState(false);
  const { language, t } = useI18n();
  const invoiceStatusesLookup = useLookupValues("invoice_statuses");
  const lookupStatusOptions = (invoiceStatusesLookup.data?.values || []).map(
    (row) => ({
      value: row.value,
      label: row.label,
    }),
  );
  const statusOptions = lookupStatusOptions;
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["invoices-admin", status],
    queryFn: async () =>
      unwrap(
        await api.get("/invoices", {
          params: status !== "all" ? { status } : {},
        }),
      ),
  });
  const { data: years } = useQuery({
    queryKey: ["ay"],
    queryFn: async () => unwrap(await api.get("/academic-years")),
  });
  const { data: grades } = useQuery({
    queryKey: ["grades"],
    queryFn: async () => unwrap(await api.get("/grades")),
  });
  const { data: students } = useQuery({
    queryKey: ["students-for-invoice"],
    queryFn: async () => unwrap(await api.get("/students", { params: { limit: 500 } })),
    enabled: canCreateStudentFeePayment,
  });
  const { data: invoiceSchedule } = useQuery({
    queryKey: ["invoice-schedule"],
    queryFn: async () => unwrap(await api.get("/invoices/schedule")),
  });
  const qc = useQueryClient();

  const genMut = useMutation({
    mutationFn: async ({
      academic_year_id,
      grade_ids,
      period_label,
      installment_id,
    }) =>
      unwrap(
        await api.post("/invoices/generate", {
          academic_year_id,
          grade_ids,
          period_label,
          ...(installment_id ? { installment_id } : {}),
        }),
      ),
    onSuccess: (d) => {
      toast.success(t("finance.invoicesGenerated", { count: d.created }));
      refetch();
      setGenOpen(false);
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const applyVat = useMutation({
    mutationFn: async (invoice_id) =>
      unwrap(await api.post(`/vat/apply-to-invoice/${invoice_id}`)),
    onSuccess: () => {
      toast.success(t("finance.vatApplied"));
      refetch();
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const downloadPdf = (inv) => {
    const token = localStorage.getItem("eduos_token");
    if (!token) return;
    const url = `${process.env.REACT_APP_BACKEND_URL || ""}/api/v1/fees/invoices/${inv.id}/pdf?lang=${language}`;
    const xhr = new XMLHttpRequest();
    xhr.open("GET", url);
    xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    xhr.responseType = "blob";
    xhr.onload = () => {
      const blob = new Blob([xhr.response], { type: "application/pdf" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `invoice-${inv.invoice_no}.pdf`;
      a.click();
    };
    xhr.send();
  };

  return (
    <div className="mt-6 space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-48" data-testid="invoice-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("finance.allStatuses")}</SelectItem>
            {statusOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="ml-auto">
          {(canWrite || canCreateStudentFeePayment) && (
            <div className="flex items-center gap-2 flex-wrap justify-end">
              {canCreateStudentFeePayment && (
                <Dialog open={feePayOpen} onOpenChange={setFeePayOpen}>
                  <DialogTrigger asChild>
                    <Button
                      variant="outline"
                      data-testid="student-fee-payment-btn"
                    >
                      <Wallet className="h-4 w-4 mr-1.5" />{" "}
                      {t("finance.studentFeePayment")}
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-xl">
                    <DialogHeader>
                      <DialogTitle className="font-display">
                        {t("finance.studentFeePayment")}
                      </DialogTitle>
                    </DialogHeader>
                    <StudentFeePaymentForm
                      students={students}
                      years={years}
                      onSuccess={(invoices) => {
                        setFeePayOpen(false);
                        refetch();
                        ["student-fee-selection", "fee-summary", "fee-plan", "report-term-balance"].forEach((key) =>
                          qc.invalidateQueries({ queryKey: [key] }),
                        );
                        (invoices || []).forEach((inv) => downloadPdf(inv));
                        toast.success(t("finance.studentFeePaymentDone"));
                      }}
                    />
                  </DialogContent>
                </Dialog>
              )}
              {canWrite && (
                <Dialog open={genOpen} onOpenChange={setGenOpen}>
                  <DialogTrigger asChild>
                    <Button
                      className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white"
                      data-testid="generate-btn"
                    >
                      <FileText className="h-4 w-4 mr-1.5" />{" "}
                      {t("finance.generateInvoices")}
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle className="font-display">
                        {t("finance.generateInvoices")}
                      </DialogTitle>
                    </DialogHeader>
                    <GenerateForm
                      years={years}
                      grades={grades}
                      schedule={invoiceSchedule?.schedule || []}
                      onSubmit={(v) => genMut.mutate(v)}
                      loading={genMut.isPending}
                    />
                  </DialogContent>
                </Dialog>
              )}
            </div>
          )}
        </div>
      </div>

      <Card className="border-[var(--subtle)]">
        <CardContent className="p-0 overflow-x-auto">
          {isLoading ? (
            <Skeleton className="h-64" />
          ) : (
            <table className="w-full min-w-[720px] text-sm grid-table">
              <thead>
                <tr>
                  <th className="text-left px-4 py-3">
                    {t("finance.invoiceNumber")}
                  </th>
                  <th className="text-left px-4 py-3">
                    {t("finance.student")}
                  </th>
                  <th className="text-left px-4 py-3">{t("finance.period")}</th>
                  <th className="text-right px-4 py-3">{t("finance.total")}</th>
                  <th className="text-right px-4 py-3">
                    {t("finance.balance")}
                  </th>
                  <th className="text-left px-4 py-3">{t("finance.status")}</th>
                  <th className="text-left px-4 py-3">{t("finance.action")}</th>
                </tr>
              </thead>
              <tbody>
                {(data || []).map((inv) => (
                  <tr key={inv.id} data-testid={`invoice-${inv.invoice_no}`}>
                    <td className="px-4 py-3 font-mono-num text-xs">
                      {inv.invoice_no}
                    </td>
                    <td className="px-4 py-3">
                      {inv.student
                        ? `${inv.student.first_name} ${inv.student.last_name}`
                        : "—"}
                    </td>
                    <td className="px-4 py-3">{displayPeriodLabel(inv.period_label, language)}</td>
                    <td className="px-4 py-3 text-right font-mono-num">
                      <div>{formatMoney(inv.total_paise)}</div>
                      {inv.discount_paise > 0 && (
                        <div className="text-xs text-[var(--success)]">
                          {t("finance.discount")}: -
                          {formatMoney(inv.discount_paise)}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-mono-num font-semibold">
                      {formatMoney(inv.balance_paise)}
                    </td>
                    <td className="px-4 py-3">
                      <Badge
                        className={`${tone(statusTone(inv.status))} border-0 capitalize`}
                      >
                        {t(`guardian.statuses.${inv.status}`)}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => downloadPdf(inv)}
                          data-testid={`pdf-${inv.invoice_no}`}
                          className="h-8"
                        >
                          <Download className="h-3.5 w-3.5 mr-1" />{" "}
                          {t("finance.pdf")}
                        </Button>
                        {canWrite &&
                          !inv.tax_paise &&
                          inv.discount_application_state !== "pending" &&
                          inv.payment_initiation_state !== "pending" && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => applyVat.mutate(inv.id)}
                              disabled={applyVat.isPending}
                              data-testid={`vat-${inv.invoice_no}`}
                              className="h-8"
                            >
                              {t("finance.applyVat")}
                            </Button>
                          )}
                        {canWrite &&
                          inv.balance_paise > 0 &&
                          inv.discount_application_state !== "pending" &&
                          inv.payment_initiation_state !== "pending" && (
                            <Button
                              size="sm"
                              onClick={() => setPayOpen(inv)}
                              data-testid={`pay-${inv.invoice_no}`}
                              className="bg-[var(--brand)] hover:bg-[var(--brand-hover)] text-white h-8"
                            >
                              {t("finance.recordPayment")}
                            </Button>
                          )}
                        {canWrite &&
                          inv.status !== "voided" &&
                          !Number(inv.paid_paise || 0) && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setVoidOpen(inv)}
                              data-testid={`void-${inv.invoice_no}`}
                              className="h-8 text-[var(--error)]"
                            >
                              {t("finance.void")}
                            </Button>
                          )}
                        {canWrite &&
                          Number(inv.paid_paise || 0) > 0 &&
                          inv.status !== "voided" &&
                          inv.discount_application_state !== "pending" &&
                          inv.payment_initiation_state !== "pending" && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setRefundOpen(inv)}
                              data-testid={`refund-${inv.invoice_no}`}
                              className="h-8"
                            >
                              <RefreshCcw className="h-3.5 w-3.5 mr-1" />{" "}
                              {t("finance.recordRefund")}
                            </Button>
                          )}
                        {canWrite &&
                          Number(inv.paid_paise || 0) > 0 &&
                          inv.status !== "voided" &&
                          inv.discount_application_state !== "pending" &&
                          inv.payment_initiation_state !== "pending" && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setRefundAndCancelOpen(inv)}
                              data-testid={`refund-cancel-${inv.invoice_no}`}
                              className="h-8 text-[var(--error)]"
                            >
                              {t("finance.refundAndCancel")}
                            </Button>
                          )}
                        {canWrite &&
                          inv.balance_paise > 0 &&
                          !Number(inv.paid_paise || 0) &&
                          inv.status !== "partially_paid" &&
                          inv.discount_application_state !== "pending" &&
                          inv.payment_initiation_state !== "pending" &&
                          !inv.discount_applied &&
                          !inv.sibling_discount_applied && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setDiscountOpen(inv)}
                              data-testid={`discount-${inv.invoice_no}`}
                              className="h-8"
                            >
                              <Tag className="h-3.5 w-3.5 mr-1" />{" "}
                              {t("finance.applyDiscount")}
                            </Button>
                          )}
                      </div>
                    </td>
                  </tr>
                ))}
                {(data || []).length === 0 && !isLoading && (
                  <tr>
                    <td
                      colSpan={7}
                      className="text-center py-10 text-[var(--text-muted)]"
                    >
                      {t("finance.noData")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!payOpen} onOpenChange={(v) => !v && setPayOpen(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display">
              {t("finance.recordPayment")}
            </DialogTitle>
          </DialogHeader>
          {payOpen && (
            <PayForm
              invoice={payOpen}
              onSuccess={() => {
                setPayOpen(null);
                refetch();
                qc.invalidateQueries();
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!voidOpen} onOpenChange={(v) => !v && setVoidOpen(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display">
              {t("finance.voidInvoice")}
            </DialogTitle>
          </DialogHeader>
          {voidOpen && (
            <VoidForm
              invoice={voidOpen}
              onSuccess={() => {
                setVoidOpen(null);
                refetch();
                qc.invalidateQueries();
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!refundOpen} onOpenChange={(v) => !v && setRefundOpen(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display">
              {t("finance.recordRefund")}
            </DialogTitle>
          </DialogHeader>
          {refundOpen && (
            <InvoiceRefundForm
              invoice={refundOpen}
              onSuccess={() => {
                setRefundOpen(null);
                refetch();
                qc.invalidateQueries({ queryKey: ["refunds"] });
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!refundAndCancelOpen} onOpenChange={(v) => !v && setRefundAndCancelOpen(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display">
              {t("finance.refundAndCancelInvoice")}
            </DialogTitle>
          </DialogHeader>
          {refundAndCancelOpen && (
            <InvoiceRefundForm
              invoice={refundAndCancelOpen}
              cancelAfter
              onSuccess={() => {
                setRefundAndCancelOpen(null);
                refetch();
                qc.invalidateQueries();
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!discountOpen}
        onOpenChange={(v) => !v && setDiscountOpen(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display">
              {t("finance.applyDiscount")}
            </DialogTitle>
          </DialogHeader>
          {discountOpen && (
            <DiscountApplyForm
              invoice={discountOpen}
              onSuccess={() => {
                setDiscountOpen(null);
                refetch();
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function GenerateForm({ years, grades, schedule = [], onSubmit, loading }) {
  const { t, language } = useI18n();
  const currentYear =
    (years || []).find((y) => y.is_current) || (years || [])[0];
  const [form, setForm] = useState({
    academic_year_id: currentYear?.id || "",
    grade_ids: [],
    period_label: schedule[0]?.period_label || "Term 2",
    installment_id: schedule[0]?.installment_id || "",
  });
  const visibleSchedule = schedule.filter(
    (item) =>
      !item.academic_year_id || item.academic_year_id === form.academic_year_id,
  );
  React.useEffect(() => {
    if (currentYear)
      setForm((f) =>
        f.academic_year_id ? f : { ...f, academic_year_id: currentYear.id },
      );
  }, [currentYear]);
  React.useEffect(() => {
    if (
      schedule.length &&
      !visibleSchedule.some(
        (item) => item.installment_id === form.installment_id,
      )
    ) {
      const first = visibleSchedule[0];
      const nextInstallment = first?.installment_id || "";
      const nextPeriod = first?.period_label || "Term 2";
      if (
        nextInstallment !== form.installment_id ||
        nextPeriod !== form.period_label
      ) {
        setForm((f) => ({
          ...f,
          installment_id: nextInstallment,
          period_label: nextPeriod,
        }));
      }
    }
  }, [
    schedule,
    form.installment_id,
    form.academic_year_id,
    form.period_label,
    visibleSchedule,
  ]);
  return (
    <div className="space-y-3">
      <div>
        <Label className="text-xs uppercase">{t("finance.academicYear")}</Label>
        <Select
          value={form.academic_year_id}
          onValueChange={(v) => setForm({ ...form, academic_year_id: v })}
        >
          <SelectTrigger className="mt-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(years || []).map((y) => (
              <SelectItem key={y.id} value={y.id}>
                {y.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs uppercase">{t("finance.periodLabel")}</Label>
        {visibleSchedule.length ? (
          <Select
            value={form.installment_id}
            onValueChange={(value) => {
              const selected = visibleSchedule.find(
                (item) => item.installment_id === value,
              );
              setForm({
                ...form,
                installment_id: value,
                period_label: selected?.period_label || "",
              });
            }}
          >
            <SelectTrigger
              className="mt-1"
              data-testid="invoice-schedule-select"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {visibleSchedule.map((item) => (
                <SelectItem
                  key={item.installment_id}
                  value={item.installment_id}
                >
                  {item.period_label} · {t("finance.dueDate")}{" "}
                  {formatDate(item.due_date, language)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Input
            value={form.period_label}
            onChange={(e) => setForm({ ...form, period_label: e.target.value })}
            className="mt-1"
            placeholder={t("finance.periodPlaceholder")}
          />
        )}
      </div>
      {visibleSchedule.length > 0 &&
        (() => {
          const selected = visibleSchedule.find(
            (item) => item.installment_id === form.installment_id,
          );
          return selected ? (
            <div
              className="rounded bg-[var(--bg)] p-3 text-xs text-[var(--text-muted)]"
              data-testid="invoice-schedule-summary"
            >
              {t("finance.total")}:{" "}
              <span className="font-mono-num font-semibold text-[var(--text-primary)]">
                {formatMoney(
                  selected.expected_total_paise ||
                    selected.expected_total_fils ||
                    0,
                )}
              </span>
              <span className="mx-2">·</span>
              {t("finance.dueDate")}: {formatDate(selected.due_date, language)}
            </div>
          ) : null;
        })()}
      <div className="text-xs text-[var(--text-muted)]">
        {t("finance.leaveGradesEmpty")}
      </div>
      <DialogFooter>
        <Button
          onClick={() => onSubmit(form)}
          disabled={loading || !form.academic_year_id}
          className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white"
          data-testid="submit-generate"
        >
          {loading ? t("finance.generating") : t("finance.generate")}
        </Button>
      </DialogFooter>
    </div>
  );
}

function StudentFeePaymentForm({ students, years, onSuccess }) {
  const { t, language } = useI18n();
  const { user } = useAuth();
  const invoiceStatusesLookup = useLookupValues("invoice_statuses");
  const currentYear =
    (years || []).find((y) => y.is_current) || (years || [])[0];
  const [studentPickerOpen, setStudentPickerOpen] = useState(false);
  const [form, setForm] = useState({
    student_id: "",
    academic_year_id: currentYear?.id || "",
    issue_date: today(),
    due_date: today(),
  });
  const [termSelections, setTermSelections] = useState({});
  const [otherFees, setOtherFees] = useState([]);
  // Custom entries: `${fee_head_id}:${term}` for term fees, fee_head_id for one-time fees.
  const [customTerms, setCustomTerms] = useState({});
  const [customOthers, setCustomOthers] = useState({});
  // Months picked per monthly fee head: { [fee_head_id]: ["YYYY-MM", ...] }.
  const [monthSelections, setMonthSelections] = useState({});
  // One purchase per dialog: a fee charged when bought can be charged again in a later payment.
  const [purchaseRef] = useState(() => (globalThis.crypto?.randomUUID ? globalThis.crypto.randomUUID() : `${Date.now()}-${Math.random()}`));
  // A resend of the same payment reuses its Idempotency-Key (see submissionKey).
  const submitKey = React.useRef({});
  const [paymentMode, setPaymentMode] = useState("manual");
  const [utr, setUtr] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("paid");
  const [uniformSize, setUniformSize] = useState("");
  const [discountCodes, setDiscountCodes] = useState([]);
  const [customDiscountForm, setCustomDiscountForm] = useState({ name: "", discount_type: "percentage", percentage: "", amount: "" });
  const roles = user?.roles || [];
  // Keep this aligned with the backend student_fee_writer dependency. The
  // effective finance capability is server-authoritative; role names alone
  // differ between legacy and current tenant assignments.
  const discountsEnabled =
    roles.some((role) => STUDENT_FEE_PAYMENT_ROLES.includes(role)) && hasCapability(user, CAPABILITY.FINANCE_MANAGE);
  const discountLookup = useLookupValues("fee_discount_types", { enabled: discountsEnabled });
  const uniformSizesLookup = useLookupValues("uniform_sizes");
  const customReasons = useLookupValues("fee_custom_reasons");
  const { data: discountRules, isLoading: discountRulesLoading, isError: discountRulesError } = useQuery({
    queryKey: ["discount-rules"],
    queryFn: async () => unwrap(await api.get("/fees/discount-rules")),
    enabled: discountsEnabled,
  });

  React.useEffect(() => {
    if (currentYear && !form.academic_year_id) {
      setForm((v) => ({ ...v, academic_year_id: currentYear.id }));
    }
  }, [currentYear, form.academic_year_id]);

  const selectedStudent = (students || []).find(
    (s) => s.id === form.student_id,
  );

  const { data: selection, isFetching: selectionLoading } = useQuery({
    queryKey: ["student-fee-selection", form.student_id, form.academic_year_id],
    queryFn: async () =>
      unwrap(
        await api.get("/invoices/student-fee-selection", {
          params: {
            student_id: form.student_id,
            academic_year_id: form.academic_year_id,
          },
        }),
      ),
    enabled: Boolean(form.student_id && form.academic_year_id),
  });

  const lines = selection?.lines || [];
  // Optional fee heads are offered only once enabled in the student's fee plan.
  const termLines = lines.filter((l) => l.frequency === "term" && l.enabled !== false);
  const monthlyLines = lines.filter((l) => l.frequency === "monthly" && l.enabled !== false);
  const otherFeeLines = lines.filter((l) => l.frequency !== "term" && l.frequency !== "monthly" && l.enabled !== false);
  const optionalOffLines = lines.filter((l) => l.enabled === false);

  // Periods already invoiced for this student, per fee head (from the dues).
  const chargedLabels = React.useMemo(() => chargedLabelsFromDues(selection?.dues), [selection]);
  const optionCharged = (line, term, optionId) => isOptionCharged(chargedLabels, line, term, optionId);
  const termMonths = (term) => {
    const months = (selection?.periods || []).find((p) => p.key === `term${term}`)?.months || [];
    return months.length ? ` · ${months.map((m) => monthLabel(m, language)).join(" ")}` : "";
  };

  // Dues that have already fallen due are pre-selected once per student, from
  // fresh data only: a cached copy can predate the last payment.
  const prefilledFor = React.useRef("");
  React.useEffect(() => {
    const key = `${form.student_id}:${form.academic_year_id}`;
    if (!selection || selectionLoading || prefilledFor.current === key) return;
    prefilledFor.current = key;
    const next = overdueTermSelections(selection.dues, selection.lines, today(), chargedLabels);
    if (Object.keys(next).length) setTermSelections(next);
  }, [selection, selectionLoading, chargedLabels, form.student_id, form.academic_year_id]);

  // An option charged since it was picked can no longer be submitted.
  React.useEffect(() => {
    setTermSelections((prev) => {
      const next = dropChargedSelections(prev, selection?.lines, chargedLabels);
      return Object.keys(next).length === Object.keys(prev).length ? prev : next;
    });
  }, [chargedLabels, selection]);

  const termOptionsFor = (line, term) => {
    const amounts = line.termAmounts || {};
    const base = amounts[`term${term}`] || 0;
    const per = line.installmentsPerTerm || 1;
    const opts = [
      {
        id: "full",
        label: `${t("finance.fullTerm")} ${formatMoney(base)}`,
        amount: base,
      },
    ];
    for (let n = 1; n <= per; n++) {
      opts.push({
        id: `installment_${n}`,
        label: `${t("finance.installment")} ${n} · ${formatMoney(Math.floor(base / per))}`,
        amount: Math.floor(base / per),
      });
    }
    return opts;
  };

  const selectedTotalPaise = React.useMemo(() => {
    let sum = 0;
    for (const [key, optionId] of Object.entries(termSelections)) {
      const [fee_head_id, termStr] = key.split(":");
      const line = termLines.find((l) => l.fee_head_id === fee_head_id);
      if (!line) continue;
      const term = parseInt(termStr, 10);
      const opt = termOptionsFor(line, term).find((o) => o.id === optionId);
      if (opt) sum += opt.amount;
    }
    for (const hid of otherFees) {
      const line = otherFeeLines.find((l) => l.fee_head_id === hid);
      if (line) sum += line.amount_paise || 0;
    }
    for (const entry of [...Object.values(customTerms), ...Object.values(customOthers)]) {
      const value = Math.round(parseFloat(entry.amount) * 100);
      if (Number.isFinite(value) && value > 0) sum += value;
    }
    for (const [hid, months] of Object.entries(monthSelections)) {
      const line = monthlyLines.find((l) => l.fee_head_id === hid);
      if (line) sum += (line.amount_paise || 0) * months.length;
    }
    return sum;
  }, [termSelections, otherFees, termLines, otherFeeLines, customTerms, customOthers, monthSelections, monthlyLines]);

  const tuitionTotalPaise = React.useMemo(() => {
    let sum = 0;
    for (const [key, optionId] of Object.entries(termSelections)) {
      const [fee_head_id, termStr] = key.split(":");
      const line = termLines.find((l) => l.fee_head_id === fee_head_id);
      if (!line || !line.is_tuition) continue;
      const term = parseInt(termStr, 10);
      const opt = termOptionsFor(line, term).find((o) => o.id === optionId);
      if (opt) sum += opt.amount;
    }
    return sum;
  }, [termSelections, termLines]);

  const blankCustom = { months: [], amount: "", reason: "", note: "", save: false, items: {} };
  const toggleCustomTerm = (line, term) => {
    const key = `${line.fee_head_id}:${term}`;
    setCustomTerms((prev) => {
      const next = { ...prev };
      if (next[key]) delete next[key];
      else next[key] = { ...blankCustom };
      return next;
    });
    setTermSelections((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };
  const toggleCustomOther = (line) => {
    setCustomOthers((prev) => {
      const next = { ...prev };
      if (next[line.fee_head_id]) delete next[line.fee_head_id];
      else next[line.fee_head_id] = { ...blankCustom };
      return next;
    });
    setOtherFees((prev) => prev.filter((id) => id !== line.fee_head_id));
  };
  const termPeriodMonths = (term) => (selection?.periods || []).find((p) => p.key === `term${term}`)?.months || [];
  const customProblems = [
    ...Object.entries(customTerms).map(([key, entry]) => {
      const [hid, termStr] = key.split(":");
      const line = termLines.find((l) => l.fee_head_id === hid);
      const standard = line?.standard_termAmounts?.[`term${termStr}`] ?? 0;
      return line ? customEntryProblem(line, standard, entry, { term: true }) : "missing";
    }),
    ...Object.entries(customOthers).map(([hid, entry]) => {
      const line = otherFeeLines.find((l) => l.fee_head_id === hid);
      return line ? customEntryProblem(line, line.standard_amount_paise ?? 0, entry, { needsItems: Boolean(line.inventory_mapped) }) : "missing";
    }),
  ].filter(Boolean);

  const toggleTerm = (line, term, option) => {
    const key = `${line.fee_head_id}:${term}`;
    setCustomTerms((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setTermSelections((prev) => {
      const next = { ...prev };
      if (next[key] === option) delete next[key];
      else next[key] = option;
      return next;
    });
  };

  const toggleMonth = (feeHeadId, month) => {
    setMonthSelections((prev) => {
      const picked = prev[feeHeadId] || [];
      const months = picked.includes(month) ? picked.filter((m) => m !== month) : [...picked, month].sort();
      const next = { ...prev, [feeHeadId]: months };
      if (!months.length) delete next[feeHeadId];
      return next;
    });
  };

  const toggleOther = (feeHeadId) => {
    setCustomOthers((prev) => {
      if (!prev[feeHeadId]) return prev;
      const next = { ...prev };
      delete next[feeHeadId];
      return next;
    });
    setOtherFees((prev) =>
      prev.includes(feeHeadId)
        ? prev.filter((id) => id !== feeHeadId)
        : [...prev, feeHeadId],
    );
  };

  const mut = useMutation({
    mutationFn: async () => {
      const selectedTerms = Object.entries(termSelections).map(
        ([key, option]) => {
          const [fee_head_id, termStr] = key.split(":");
          return { fee_head_id, term: parseInt(termStr, 10), option };
        },
      );
      const payload = {
        student_id: form.student_id,
        academic_year_id: form.academic_year_id,
        structure_id: selection?.structure?.id,
        selected_terms: selectedTerms,
        other_fee_head_ids: otherFees,
        selected_months: selectedMonthsPayload(monthSelections),
        purchase_ref: purchaseRef,
        custom_lines: [
          ...Object.entries(customTerms).map(([key, entry]) => {
            const [hid, termStr] = key.split(":");
            return customLinePayload(hid, parseInt(termStr, 10), entry);
          }),
          ...Object.entries(customOthers).map(([hid, entry]) => customLinePayload(hid, null, entry)),
        ],
        payment_method: paymentMode === "online" ? "online" : paymentMode === "pos" ? "pos" : "cash",
        ...((paymentMode === "online" || paymentMode === "pos") ? { utr_reference: utr } : {}),
        payment_status: paymentStatus,
        ...(uniformSize ? { uniform_size: uniformSize } : {}),
        discount_codes: discountCodes,
        ...(customDiscountForm.name.trim() ? {
          custom_discount: {
            name: customDiscountForm.name.trim(),
            discount_type: customDiscountForm.discount_type,
            ...(customDiscountForm.discount_type === "percentage"
              ? { percentage: Number(customDiscountForm.percentage) }
              : { amount_paise: Math.round(Number(customDiscountForm.amount) * 100) }),
          },
        } : {}),
        issue_date: form.issue_date,
        due_date: form.due_date,
      };
      return unwrap(
        await api.post("/invoices/student-fee-payment", payload, {
          headers: { "Idempotency-Key": submissionKey(submitKey.current, payload) },
        }),
      );
    },
    onSuccess: (d) => onSuccess(d.invoices),
    onError: (e) => toast.error(errMsg(e)),
  });

  const hasStudentAndYear = Boolean(form.student_id && form.academic_year_id);
  const hasSelection = Boolean(
    (termSelections && Object.keys(termSelections).length) || otherFees.length ||
    Object.keys(customTerms).length || Object.keys(customOthers).length ||
    Object.keys(monthSelections).length,
  );
  const onlineInvalid = (paymentMode === "online" || paymentMode === "pos") && !utr.trim();
  const discountOptions = discountLookup.data?.values || [];
  const discountDetails = (row) => {
    const metadata = row.metadata || {};
    if (metadata.custom_discount === true && metadata.discount_type === "amount") {
      return { discount_type: "amount", amount_paise: Number(metadata.amount_paise) };
    }
    const bps = metadata.custom_discount === true ? metadata.percentage_bps : (discountRules || {})[metadata.rule_field];
    return Number.isFinite(Number(bps)) ? { discount_type: "percentage", percentage_bps: Number(bps) } : null;
  };
  const selectedDiscounts = discountCodes.map((code) => {
    const row = discountOptions.find((option) => option.value === code);
    return row ? discountDetails(row) : null;
  }).filter(Boolean);
  const customAmountPaise = Math.round(Number(customDiscountForm.amount) * 100);
  const customDiscountValid = !customDiscountForm.name.trim() || (
    customDiscountForm.name.trim().length >= 2 &&
    (customDiscountForm.discount_type === "percentage"
      ? customDiscountForm.percentage !== "" && Number(customDiscountForm.percentage) >= 0 && Number(customDiscountForm.percentage) <= 100
      : customDiscountForm.amount !== "" && Number.isFinite(customAmountPaise) && customAmountPaise > 0)
  );
  if (customDiscountForm.name.trim() && customDiscountValid) {
    selectedDiscounts.push(
      customDiscountForm.discount_type === "percentage"
        ? { discount_type: "percentage", percentage_bps: Math.round(Number(customDiscountForm.percentage) * 100) }
        : { discount_type: "amount", amount_paise: customAmountPaise },
    );
  }
  const selectedDiscountBps = Math.min(10000, selectedDiscounts.reduce(
    (sum, discount) => sum + (discount.discount_type === "percentage" ? discount.percentage_bps : 0), 0,
  ));
  // Discounts apply to the tuition fee only (is_tuition lines); if no tuition
  // is selected, no discount is applied. Other fees are never discounted.
  const discountBase = tuitionTotalPaise > 0 ? tuitionTotalPaise : 0;
  const percentageDiscountPaise = discountBase > 0 ? Math.floor((discountBase * selectedDiscountBps + 5000) / 10000) : 0;
  const fixedDiscountPaise = discountBase > 0
    ? Math.min(
        Math.max(0, discountBase - percentageDiscountPaise),
        selectedDiscounts.reduce((sum, discount) => sum + (discount.discount_type === "amount" ? discount.amount_paise : 0), 0),
      )
    : 0;
  const discountPaise = percentageDiscountPaise + fixedDiscountPaise;
  const payableTotalPaise = Math.max(0, selectedTotalPaise - discountPaise);

  const breakdownItems = React.useMemo(() => {
    const items = [];
    const push = (key, label, gross, isTuition) =>
      items.push({ key, label, gross, isTuition, net: gross, discount: 0 });
    for (const [key, optionId] of Object.entries(termSelections)) {
      const [fee_head_id, termStr] = key.split(":");
      const line = termLines.find((l) => l.fee_head_id === fee_head_id);
      if (!line) continue;
      const term = parseInt(termStr, 10);
      const opt = termOptionsFor(line, term).find((o) => o.id === optionId);
      if (opt) push(key, line.fee_head_name, opt.amount, Boolean(line.is_tuition));
    }
    for (const hid of otherFees) {
      const line = otherFeeLines.find((l) => l.fee_head_id === hid);
      if (line) push(hid, line.fee_head_name, line.amount_paise || 0, false);
    }
    for (const [hid, months] of Object.entries(monthSelections)) {
      const line = monthlyLines.find((l) => l.fee_head_id === hid);
      for (const month of line ? months : []) {
        push(`month-${hid}-${month}`, `${line.fee_head_name} · ${monthLabel(month, language)} ${month.slice(0, 4)}`, line.amount_paise || 0, false);
      }
    }
    // Custom amounts are listed as entered and never discounted.
    for (const [key, entry] of [...Object.entries(customTerms), ...Object.entries(customOthers)]) {
      const hid = key.split(":")[0];
      const line = [...termLines, ...otherFeeLines].find((l) => l.fee_head_id === hid);
      const value = Math.round(parseFloat(entry.amount) * 100);
      if (line && Number.isFinite(value) && value > 0) push(`custom-${key}`, `${line.fee_head_name} · ${t("feePlan.custom")}`, value, false);
    }
    const tuitionItems = items.filter((i) => i.isTuition);
    const tuitionGross = tuitionItems.reduce((s, i) => s + i.gross, 0);
    if (discountPaise > 0 && tuitionGross > 0) {
      let remaining = discountPaise;
      tuitionItems.forEach((item, index) => {
        if (index === tuitionItems.length - 1) {
          item.discount = remaining;
        } else {
          item.discount = Math.min(item.gross, Math.floor((discountPaise * item.gross) / tuitionGross));
          remaining -= item.discount;
        }
        item.net = Math.max(0, item.gross - item.discount);
      });
    }
    return items;
  }, [termSelections, otherFees, termLines, otherFeeLines, discountPaise, customTerms, customOthers, monthSelections, monthlyLines, language, t]);

  const discountTypeLabels = [
    ...discountCodes.map((code) => {
      const row = discountOptions.find((o) => o.value === code);
      if (!row) return null;
      const details = discountDetails(row);
      if (!details) return null;
      if (details.discount_type === "amount") {
        return `${row.label} (${formatMoney(details.amount_paise)})`;
      }
      return `${row.label} (${details.percentage_bps / 100}%)`;
    }).filter(Boolean),
    ...(customDiscountForm.name.trim() && customDiscountValid
      ? [
          customDiscountForm.discount_type === "amount"
            ? `${customDiscountForm.name.trim()} (${formatMoney(customAmountPaise)})`
            : `${customDiscountForm.name.trim()} (${customDiscountForm.percentage}%)`,
        ]
      : []),
  ];

  return (
    <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
      <div>
        <FieldLabel required>{t("finance.student")}</FieldLabel>
        <Popover open={studentPickerOpen} onOpenChange={setStudentPickerOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              role="combobox"
              aria-expanded={studentPickerOpen}
              className="mt-1 h-9 w-full justify-between font-normal"
              data-testid="fee-pay-student"
            >
              <span
                className={
                  selectedStudent
                    ? "truncate"
                    : "truncate text-[var(--text-muted)]"
                }
              >
                {selectedStudent
                  ? `${selectedStudent.first_name} ${selectedStudent.last_name} (${selectedStudent.admission_no})`
                  : t("finance.selectStudent")}
              </span>
              <span aria-hidden="true" className="ml-2 opacity-50">
                ⌄
              </span>
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            className="w-[var(--radix-popover-trigger-width)] p-0"
          >
            <Command>
              <CommandInput placeholder={t("finance.searchStudent")} />
              <CommandList>
                <CommandEmpty>{t("finance.noStudentsFound")}</CommandEmpty>
                <CommandGroup>
                  {(students || []).map((student) => (
                    <CommandItem
                      key={student.id}
                      value={`${student.first_name} ${student.last_name} ${student.admission_no}`}
                      onSelect={() => {
                        setForm({ ...form, student_id: student.id });
                        setTermSelections({});
                        setOtherFees([]);
                        setCustomTerms({});
                        setCustomOthers({});
                        setMonthSelections({});
                        setDiscountCodes([]);
                        setCustomDiscountForm({ name: "", discount_type: "percentage", percentage: "", amount: "" });
                        setStudentPickerOpen(false);
                      }}
                    >
                      {student.first_name} {student.last_name} (
                      {student.admission_no})
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        {selectedStudent && (
          <div className="mt-2">
            <Label className="text-xs uppercase">
              {t("finance.admissionNumber")}
            </Label>
            <Input
              readOnly
              value={selectedStudent.admission_no}
              className="mt-0.5 font-mono-num bg-[var(--subtle)] text-[var(--text-muted)]"
              data-testid="fee-pay-admission-no"
            />
          </div>
        )}
      </div>
      <div>
        <FieldLabel required>{t("finance.academicYear")}</FieldLabel>
        <Select
          value={form.academic_year_id}
          onValueChange={(v) => setForm({ ...form, academic_year_id: v })}
        >
          <SelectTrigger className="mt-1" data-testid="fee-pay-year">
            <SelectValue placeholder={t("finance.academicYear")} />
          </SelectTrigger>
          <SelectContent position="item-aligned">
            {(years || []).map((year) => (
              <SelectItem key={year.id} value={year.id}>
                {year.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {hasStudentAndYear && (
        <div className="space-y-4">
          {selection?.balance && (selection.balance.total_due_paise > 0 || selection.balance.upcoming_paise > 0) && (
            <div className="rounded border border-[var(--subtle)] p-3" data-testid="fee-pay-balance">
              <BalanceTotals totals={selection.balance} />
            </div>
          )}
          <div>
            <Label className="text-xs uppercase">
              {t("finance.feeSelection")}
            </Label>
            {selectionLoading ? (
              <Skeleton className="h-24 mt-2" />
            ) : (
              <div className="mt-2 space-y-4">
                {termLines.length === 0 && otherFeeLines.length === 0 && (
                  <div className="text-sm text-[var(--text-muted)]">
                    {t("finance.noFeeStructureForStudent")}
                  </div>
                )}
                {termLines.map((line) => (
                  <div
                    key={line.fee_head_id}
                    className="rounded border border-[var(--subtle)] p-3"
                  >
                    <div className="text-sm font-medium">
                      {line.fee_head_name || t("finance.lineItems")}
                      {line.is_custom && (
                        <Badge variant="outline" className="ms-2">{t("feePlan.custom")}</Badge>
                      )}
                    </div>
                    {chargeableTerms(line).map((term) => {
                      const key = `${line.fee_head_id}:${term}`;
                      return (
                        <div key={term} className="mt-2">
                          <div className="text-xs uppercase text-[var(--text-muted)] mb-1">
                            {t(`finance.term${term}`)}{termMonths(term)}
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            {termOptionsFor(line, term).map((opt) => {
                              const active = termSelections[key] === opt.id;
                              const charged = optionCharged(line, term, opt.id);
                              return (
                                <Button
                                  key={opt.id}
                                  type="button"
                                  size="sm"
                                  variant={active ? "default" : "outline"}
                                  disabled={charged}
                                  onClick={() => toggleTerm(line, term, opt.id)}
                                  data-testid={`term-btn-${line.fee_head_id}-${term}-${opt.id}`}
                                  className={
                                    active
                                      ? "bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white h-8"
                                      : "h-8"
                                  }
                                >
                                  {opt.label}
                                  {charged && ` · ${t("feePlan.alreadyCharged")}`}
                                </Button>
                              );
                            })}
                            {line.allow_custom && termPeriodMonths(term).length > 0 && (
                              <Button
                                type="button"
                                size="sm"
                                variant={customTerms[key] ? "default" : "outline"}
                                disabled={termChargedStandard(chargedLabels, line, term)}
                                onClick={() => toggleCustomTerm(line, term)}
                                data-testid={`term-custom-${line.fee_head_id}-${term}`}
                                className={customTerms[key] ? "bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white h-8" : "h-8 border-dashed"}
                              >
                                {t("customFee.custom")}
                              </Button>
                            )}
                          </div>
                          {customTerms[key] && (
                            <TermCustomPanel
                              line={line}
                              term={term}
                              months={termPeriodMonths(term)}
                              takenMonths={chargedMonths(selection?.dues, line.fee_head_id, `term${term}`)}
                              entry={customTerms[key]}
                              onChange={(entry) => setCustomTerms((prev) => ({ ...prev, [key]: entry }))}
                              reasons={customReasons.data?.values}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
                {monthlyLines.length > 0 && (
                  <div className="rounded border border-[var(--subtle)] p-3 space-y-2" data-testid="fee-pay-monthly">
                    <div className="text-sm font-medium">{t("customFee.monthlyFees")}</div>
                    {monthlyLines.map((line) => {
                      const taken = chargedMonthlyMonths(selection?.dues, line.fee_head_id);
                      const picked = monthSelections[line.fee_head_id] || [];
                      return (
                        <div key={line.fee_head_id} className="space-y-1">
                          <div className="flex items-center justify-between gap-2 text-sm">
                            <span>{line.fee_head_name}</span>
                            <span className="font-mono-num">{formatMoney(line.amount_paise)} {t("feePlan.perMonth")}</span>
                          </div>
                          <div className="text-xs text-[var(--text-muted)]">{t("customFee.pickMonths")}</div>
                          <div className="flex flex-wrap gap-1.5">
                            {(selection?.billing_months || []).map((month) => {
                              const charged = taken.has(month);
                              const on = picked.includes(month);
                              return (
                                <Button
                                  key={month}
                                  type="button"
                                  size="sm"
                                  variant={on ? "default" : "outline"}
                                  disabled={charged}
                                  onClick={() => toggleMonth(line.fee_head_id, month)}
                                  data-testid={`month-fee-${line.fee_head_id}-${month}`}
                                  title={charged ? t("feePlan.alreadyCharged") : undefined}
                                  className={on ? "bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white h-7 px-2" : "h-7 px-2"}
                                >
                                  {monthLabel(month, language)} {month.slice(2, 4)}
                                  {charged && " ✓"}
                                </Button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {otherFeeLines.length > 0 && (
                  <div className="rounded border border-[var(--subtle)] p-3 space-y-1.5">
                    <div className="text-sm font-medium">
                      {t("finance.otherFees")}
                    </div>
                    {otherFeeLines.map((line) => (
                      <div key={line.fee_head_id}>
                        <div className="flex items-center justify-between gap-2 text-sm">
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={otherFees.includes(line.fee_head_id)}
                              disabled={!line.charge_when_bought && otherFeeCharged(chargedLabels, line.fee_head_id)}
                              onChange={() => toggleOther(line.fee_head_id)}
                              className="accent-[var(--accent)]"
                              data-testid={`other-fee-${line.fee_head_id}`}
                            />
                            {line.fee_head_name || t("finance.lineItems")}
                            {line.charge_when_bought ? (
                              <span className="text-xs text-[var(--text-muted)]">· {t("feePlan.chargeWhenBought")}</span>
                            ) : otherFeeCharged(chargedLabels, line.fee_head_id) && (
                              <span className="text-xs text-[var(--text-muted)]">· {t("feePlan.alreadyCharged")}</span>
                            )}
                          </label>
                          <span className="flex items-center gap-2">
                            {line.allow_custom && (
                              <Button
                                type="button"
                                size="sm"
                                variant={customOthers[line.fee_head_id] ? "default" : "outline"}
                                onClick={() => toggleCustomOther(line)}
                                data-testid={`other-custom-${line.fee_head_id}`}
                                className={customOthers[line.fee_head_id] ? "bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white h-7" : "h-7 border-dashed"}
                              >
                                {t("customFee.custom")}
                              </Button>
                            )}
                            <span className="font-mono-num">
                              {formatMoney(line.amount_paise)}
                            </span>
                          </span>
                        </div>
                        {customOthers[line.fee_head_id] && (
                          <OtherCustomPanel
                            line={line}
                            studentId={form.student_id}
                            uniformSize={uniformSize}
                            entry={customOthers[line.fee_head_id]}
                            onChange={(entry) => setCustomOthers((prev) => ({ ...prev, [line.fee_head_id]: entry }))}
                            reasons={customReasons.data?.values}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {optionalOffLines.map((line) => (
                  <div
                    key={line.fee_head_id}
                    className="text-xs text-[var(--text-muted)]"
                    data-testid={`fee-pay-optional-off-${line.fee_head_code}`}
                  >
                    {t("feePlan.notEnabledForStudent", { name: line.fee_head_name })}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <FieldLabel>{t("finance.issueDate")}</FieldLabel>
                <Input
                  type="date"
                  value={form.issue_date}
                  onChange={(e) =>
                    setForm({ ...form, issue_date: e.target.value })
                  }
                  className="mt-1"
                />
              </div>
              <div>
                <FieldLabel>{t("finance.dueDate")}</FieldLabel>
                <Input
                  type="date"
                  value={form.due_date}
                  onChange={(e) =>
                    setForm({ ...form, due_date: e.target.value })
                  }
                  className="mt-1"
                />
              </div>
            </div>
          </div>

          <div>
            <FieldLabel>Uniform size (required when a mapped uniform fee is selected)</FieldLabel>
            <Select value={uniformSize || "__none"} onValueChange={(value) => setUniformSize(value === "__none" ? "" : value)}>
              <SelectTrigger className="mt-1" data-testid="fee-pay-uniform-size"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">Not applicable</SelectItem>
                {(uniformSizesLookup.data?.values || []).map((row) => <SelectItem key={row.value} value={row.value}>{row.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <FieldLabel>{t("finance.paymentMode")}</FieldLabel>
            <div className="flex items-center gap-2 mt-1">
              {[
                { id: "manual", label: t("finance.manualCash") },
                { id: "online", label: t("finance.online") },
                { id: "pos", label: t("finance.pos") },
              ].map((mode) => (
                <Button
                  key={mode.id}
                  type="button"
                  size="sm"
                  variant={paymentMode === mode.id ? "default" : "outline"}
                  onClick={() => setPaymentMode(mode.id)}
                  className={
                    paymentMode === mode.id
                      ? "bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white h-8"
                      : "h-8"
                  }
                  data-testid={`payment-mode-${mode.id}`}
                >
                  {mode.label}
                </Button>
              ))}
            </div>
          </div>
          {(paymentMode === "online" || paymentMode === "pos") && (
            <div>
              <Label className="text-xs uppercase">{paymentMode === "pos" ? t("finance.posReference") : t("finance.utr")}</Label>
              <Input
                value={utr}
                onChange={(e) => setUtr(e.target.value)}
                className="mt-1 font-mono-num"
                placeholder={paymentMode === "pos" ? t("finance.posReferencePlaceholder") : t("finance.utrPlaceholder")}
                data-testid="fee-pay-utr"
              />
            </div>
          )}

          <div>
            {discountsEnabled && (
              <div className="rounded border border-[var(--subtle)] p-3 space-y-2 mb-3">
                <div className="text-sm font-medium">{t("finance.studentPaymentDiscounts")}</div>
                {!hasSelection && (
                  <div className="text-sm text-[var(--text-muted)]" data-testid="discount-selection-guidance">
                    {t("finance.selectFeeBeforeDiscount")}
                  </div>
                )}
                {discountLookup.isLoading || discountRulesLoading ? (
                  <div className="text-sm text-[var(--text-muted)]" role="status">{t("finance.discountsLoading")}</div>
                ) : discountLookup.isError || discountRulesError ? (
                  <div className="text-sm text-[var(--error)]" role="alert">{t("finance.discountsLoadError")}</div>
                ) : discountOptions.length === 0 ? (
                  <div className="text-sm text-[var(--text-muted)]">{t("finance.noDiscountsAvailable")}</div>
                ) : (
                  discountOptions.map((row) => (
                    <label key={row.value} className="flex items-center justify-between gap-2 text-sm">
                      <span className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={discountCodes.includes(row.value)}
                          disabled={!hasSelection}
                          onChange={() => setDiscountCodes((current) => current.includes(row.value) ? current.filter((code) => code !== row.value) : [...current, row.value])}
                          className="accent-[var(--accent)]"
                          data-testid={`student-discount-${row.value}`}
                        />
                        {row.label}
                      </span>
                      <span className="font-mono-num">
                        {discountDetails(row)?.discount_type === "amount"
                          ? formatMoney(discountDetails(row).amount_paise)
                          : discountDetails(row) ? `${discountDetails(row).percentage_bps / 100}%` : ""}
                      </span>
                    </label>
                  ))
                )}
                {roles.includes("administrator") && (
                  <div className="border-t border-[var(--subtle)] pt-2">
                    <div className="text-xs uppercase text-[var(--text-muted)] mb-1">{t("finance.createCustomDiscount")}</div>
                    <div className="grid gap-2 sm:grid-cols-[1fr_9rem_8rem]">
                      <Input value={customDiscountForm.name} onChange={(event) => setCustomDiscountForm({ ...customDiscountForm, name: event.target.value })} placeholder={t("finance.customDiscountName")} data-testid="custom-discount-name-payment" />
                      <Select value={customDiscountForm.discount_type} onValueChange={(discount_type) => setCustomDiscountForm({ ...customDiscountForm, discount_type })}>
                        <SelectTrigger data-testid="custom-discount-type-payment"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="percentage">{t("finance.percentage")}</SelectItem><SelectItem value="amount">{t("finance.fixedAmount")}</SelectItem></SelectContent>
                      </Select>
                      {customDiscountForm.discount_type === "percentage" ? (
                        <Input type="number" min="0" max="100" step="0.01" value={customDiscountForm.percentage} onChange={(event) => setCustomDiscountForm({ ...customDiscountForm, percentage: event.target.value })} placeholder="%" data-testid="custom-discount-percentage-payment" />
                      ) : (
                        <Input type="number" min="0.01" step="0.01" value={customDiscountForm.amount} onChange={(event) => setCustomDiscountForm({ ...customDiscountForm, amount: event.target.value })} placeholder={t("finance.amount")} data-testid="custom-discount-amount-payment" />
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
            <div className="rounded-md border p-3 text-sm space-y-1">
              <div className="text-xs uppercase text-[var(--text-muted)]">{t("finance.feeBreakdown")}</div>
              {breakdownItems.length === 0 && (
                <div className="text-xs text-[var(--text-muted)]">{t("finance.noFeesSelected")}</div>
              )}
              {breakdownItems.map((item) => (
                <div key={item.key} className="flex items-center justify-between gap-2">
                  <span className="truncate text-[var(--text-muted)]">
                    {item.label}
                    {item.discount > 0 &&
                      ` (${discountTypeLabels.length ? discountTypeLabels.join(", ") : t("finance.discount")} -${formatMoney(item.discount)})`}
                  </span>
                  <span className="font-mono-num">{formatMoney(item.net)}</span>
                </div>
              ))}
              {discountPaise > 0 && (
                <div className="flex items-center justify-between gap-2 pt-0.5 text-[var(--success)]">
                  <span>{t("finance.discount")} ({t("finance.tuitionOnly")})</span>
                  <span className="font-mono-num">-{formatMoney(discountPaise)}</span>
                </div>
              )}
              <div className="flex items-center justify-between gap-2 border-t pt-1 font-semibold">
                <span>{t("finance.invoiceTotal")}</span>
                <span className="font-mono-num">{formatMoney(payableTotalPaise)}</span>
              </div>
            </div>
            <div className="mt-3">
              <Label className="text-xs uppercase">
                {t("finance.paymentStatus")}
              </Label>
              <Select
                value={paymentStatus}
                onValueChange={setPaymentStatus}
                disabled={!hasSelection}
              >
                <SelectTrigger className="mt-1" data-testid="fee-pay-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(invoiceStatusesLookup.data?.values || [])
                    .filter(
                      (row) => row.value === "paid" || row.value === "issued",
                    )
                    .map((row) => (
                      <SelectItem key={row.value} value={row.value}>
                        {row.label}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button
              onClick={() => mut.mutate()}
              disabled={
                mut.isPending ||
                !hasSelection ||
                !form.issue_date ||
                !form.due_date ||
                !customDiscountValid ||
                onlineInvalid ||
                customProblems.length > 0
              }
              className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white"
              data-testid="submit-fee-payment"
            >
              {mut.isPending
                ? t("finance.recording")
                : t("finance.recordAndGenerate")}
            </Button>
          </DialogFooter>
        </div>
      )}
    </div>
  );
}

function PayForm({ invoice, onSuccess }) {
  const { t } = useI18n();
  const paymentMethodsLookup = useLookupValues("payment_methods");
  const lookupMethodOptions = (paymentMethodsLookup.data?.values || []).map(
    (row) => ({
      value: row.value,
      label: row.label,
      requiresReference: row?.metadata?.requires_reference === true,
    }),
  );
  const methodOptions = lookupMethodOptions;
  const { data: detail } = useQuery({
    queryKey: ["invoice-detail", invoice.id],
    queryFn: async () => unwrap(await api.get(`/invoices/${invoice.id}`)),
  });
  const lines = detail?.lines?.length ? detail.lines : invoice.lines || [];
  const payableLines = lines.filter(
    (l) =>
      l.status !== "voided" &&
      (l.paid_paise || 0) < (l.total_paise ?? l.amount_paise ?? 0),
  );
  const [allocations, setAllocations] = useState({});
  const [method, setMethod] = useState("cash");
  const [reference, setReference] = useState("");
  React.useEffect(() => {
    if (!payableLines.length) return;
    setAllocations((prev) => {
      if (Object.keys(prev).length) return prev;
      const next = {};
      payableLines.forEach((l) => {
        const balance =
          (l.total_paise ?? l.amount_paise ?? 0) - (l.paid_paise || 0);
        next[l.id] = (balance / 100).toFixed(2);
      });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail?.id, lines.length]);
  const totalPaise = Object.entries(allocations).reduce(
    (sum, [id, v]) =>
      payableLines.some((l) => l.id === id)
        ? sum + (Math.round(parseFloat(v) * 100) || 0)
        : sum,
    0,
  );
  const mut = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.post("/payments/manual", {
          invoice_id: invoice.id,
          amount_paise: totalPaise,
          method,
          instrument: reference ? { reference } : undefined,
          line_allocations: payableLines
            .filter((l) => (Math.round(parseFloat(allocations[l.id]) * 100) || 0) > 0)
            .map((l) => ({
              invoice_line_id: l.id,
              amount_paise:
                Math.round(parseFloat(allocations[l.id]) * 100) || 0,
            })),
        }),
      ),
    onSuccess: () => {
      toast.success(t("finance.paymentRecorded"));
      onSuccess();
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  React.useEffect(() => {
    if (!methodOptions.length) return;
    if (methodOptions.some((option) => option.value === method)) return;
    setMethod(methodOptions[0].value);
  }, [method, methodOptions]);

  const needsRef = methodOptions.some(
    (option) => option.value === method && option.requiresReference,
  );

  return (
    <div className="space-y-3">
      <div className="rounded bg-[var(--bg)] p-3">
        <div className="text-xs text-[var(--text-muted)] font-mono-num">
          {invoice.invoice_no}
        </div>
        <div className="text-sm">
          {invoice.student
            ? `${invoice.student.first_name} ${invoice.student.last_name}`
            : ""}
        </div>
        <div className="text-xs mt-1">
          {t("finance.balance")}:{" "}
          <span className="font-mono-num font-semibold text-[var(--accent)]">
            {formatMoney(invoice.balance_paise)}
          </span>
        </div>
      </div>
      {payableLines.length > 1 && (
        <div className="text-xs text-[var(--text-muted)]">
          {t("finance.payByLineHint")}
        </div>
      )}
      {payableLines.map((l) => {
        const balance =
          (l.total_paise ?? l.amount_paise ?? 0) - (l.paid_paise || 0);
        return (
          <div key={l.id}>
            <Label className="text-xs uppercase">
              {l.fee_head_name || l.description || t("finance.lines")} —{" "}
              {t("finance.balance")}: {formatMoney(balance)}
            </Label>
            <Input
              type="number"
              step="0.01"
              value={allocations[l.id] ?? ""}
              onChange={(e) =>
                setAllocations((prev) => ({ ...prev, [l.id]: e.target.value }))
              }
              className="mt-1 font-mono-num"
              data-testid={`pay-line-${l.id.slice(0, 8)}`}
            />
          </div>
        );
      })}
      <div>
        <Label className="text-xs uppercase">{t("finance.amount")}</Label>
        <Input
          type="number"
          step="0.01"
          value={(totalPaise / 100).toFixed(2)}
          readOnly
          className="mt-1 font-mono-num"
          data-testid="pay-amount"
        />
      </div>
      <div>
        <Label className="text-xs uppercase">{t("finance.method")}</Label>
        <Select value={method} onValueChange={setMethod}>
          <SelectTrigger className="mt-1" data-testid="pay-method">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {methodOptions.map((option) => {
              const translationKey = `finance.${option.value}`;
              const translated = t(translationKey);
              const label =
                translated === translationKey ? option.label : translated;
              return (
                <SelectItem key={option.value} value={option.value}>
                  {label}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </div>
      {needsRef && (
        <div>
          <Label className="text-xs uppercase">
            {t("finance.paymentReference")}
          </Label>
          <Input
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            className="mt-1 font-mono-num"
            placeholder={t("finance.referencePlaceholder")}
            data-testid="pay-reference"
          />
        </div>
      )}
      <DialogFooter>
        <Button
          onClick={() => mut.mutate()}
          disabled={mut.isPending || (needsRef && !reference)}
          className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white"
          data-testid="submit-payment"
        >
          {mut.isPending ? t("finance.recording") : t("finance.recordPayment")}
        </Button>
      </DialogFooter>
    </div>
  );
}

function VoidForm({ invoice, onSuccess }) {
  const { t } = useI18n();
  const [reason, setReason] = useState("");
  const mut = useMutation({
    mutationFn: async () =>
      unwrap(await api.post(`/fees/invoices/${invoice.id}/void`, { reason })),
    onSuccess: () => {
      toast.success(t("finance.voided"));
      onSuccess();
    },
    onError: (e) => toast.error(errMsg(e)),
  });
  return (
    <div className="space-y-3">
      <div className="rounded bg-[var(--bg)] p-3 text-sm">
        <div className="font-mono-num text-xs text-[var(--text-muted)]">
          {invoice.invoice_no}
        </div>
        <div className="mt-1">
          {t("finance.balance")}:{" "}
          <span className="font-mono-num font-semibold">
            {formatMoney(invoice.balance_paise)}
          </span>{" "}
          → {formatMoney(0)}
        </div>
        <div className="mt-1 text-xs text-[var(--text-muted)]">
          {t("finance.voidExplainer")}
        </div>
      </div>
      <div>
        <Label className="text-xs uppercase">{t("finance.reason")}</Label>
        <Input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="mt-1"
          placeholder={t("finance.voidReasonPlaceholder")}
          data-testid="void-reason"
        />
      </div>
      <DialogFooter>
        <Button
          onClick={() => mut.mutate()}
          disabled={mut.isPending || reason.trim().length < 3}
          className="bg-[var(--error)] text-white"
          data-testid="submit-void"
        >
          {mut.isPending ? t("finance.saving") : t("finance.void")}
        </Button>
      </DialogFooter>
    </div>
  );
}

function InvoiceRefundForm({ invoice, onSuccess, cancelAfter = false }) {
  const { t } = useI18n();
  const refundMethodsLookup = useLookupValues("refund_methods");
  const methodOptions = (refundMethodsLookup.data?.values || []).map((row) => ({
    value: row.value,
    label: row.label,
  }));
  const { data: detail } = useQuery({
    queryKey: ["invoice-detail", invoice.id],
    queryFn: async () => unwrap(await api.get(`/invoices/${invoice.id}`)),
  });
  const lines = detail?.lines?.length ? detail.lines : invoice.lines || [];
  const refundableLines = lines.filter(
    (l) =>
      l.is_refundable && (l.paid_paise || 0) - (l.refunded_paise || 0) > 0,
  );
  const blockedLines = lines.filter(
    (l) => !l.is_refundable && (l.paid_paise || 0) > 0,
  );
  const [lineAmounts, setLineAmounts] = useState({});
  const [method, setMethod] = useState("bank_transfer");
  const [reason, setReason] = useState("");
  const [invoiceLevel, setInvoiceLevel] = useState(cancelAfter || !refundableLines.length);
  const [invoiceAmount, setInvoiceAmount] = useState(
    Number(invoice.paid_paise || 0) > 0
      ? (Number(invoice.paid_paise || 0) / 100).toFixed(2)
      : "",
  );
  React.useEffect(() => {
    if (!refundableLines.length) return;
    setLineAmounts((prev) => {
      if (Object.keys(prev).length) return prev;
      const next = {};
      refundableLines.forEach((l) => {
        const refundable =
          l.refundable_paise ?? (l.paid_paise || 0) - (l.refunded_paise || 0);
        next[l.id] = (refundable / 100).toFixed(2);
      });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail?.id, lines.length]);
  const totalPaise = Object.entries(lineAmounts).reduce(
    (sum, [id, v]) =>
      refundableLines.some((l) => l.id === id)
        ? sum + (Math.round(parseFloat(v) * 100) || 0)
        : sum,
    0,
  );
  const invoiceAmountPaise = Math.round(parseFloat(invoiceAmount) * 100) || 0;
  const amountPaise = invoiceLevel ? invoiceAmountPaise : totalPaise;
  const refundableCapacity = refundableLines.reduce(
    (sum, line) =>
      sum +
      (line.refundable_paise ??
        (line.paid_paise || 0) - (line.refunded_paise || 0)),
    0,
  );
  const needsInvoiceLevel =
    !refundableLines.length ||
    refundableCapacity < Number(invoice.paid_paise || 0);
  React.useEffect(() => {
    setInvoiceLevel(cancelAfter || !refundableLines.length || needsInvoiceLevel);
    setInvoiceAmount(
      Number(invoice.paid_paise || 0) > 0
        ? (Number(invoice.paid_paise || 0) / 100).toFixed(2)
        : "",
    );
    // Reset the form when a different invoice dialog is mounted.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice.id, detail?.id, cancelAfter, needsInvoiceLevel]);
  const fullRefundSelected = amountPaise === Number(invoice.paid_paise || 0);
  const mut = useMutation({
    mutationFn: async () => {
      const payload = {
        invoice_id: invoice.id,
        amount_paise: amountPaise,
        reason: reason.trim(),
        method,
      };
      if (!invoiceLevel) {
        payload.line_allocations = refundableLines
          .filter(
            (l) => (Math.round(parseFloat(lineAmounts[l.id]) * 100) || 0) > 0,
          )
          .map((l) => ({
            invoice_line_id: l.id,
            amount_paise:
              Math.round(parseFloat(lineAmounts[l.id]) * 100) || 0,
          }));
      }
      return unwrap(
        await api.post(
          cancelAfter
            ? `/fees/invoices/${invoice.id}/refund-and-cancel`
            : "/fees/refunds",
          payload,
        ),
      );
    },
    onSuccess: () => {
      toast.success(t(cancelAfter ? "finance.refundAndCancelled" : "finance.refundRecorded"));
      onSuccess();
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  React.useEffect(() => {
    if (!methodOptions.length) return;
    if (methodOptions.some((option) => option.value === method)) return;
    setMethod(methodOptions[0].value);
  }, [method, methodOptions]);

  return (
    <div className="space-y-3">
      <div className="rounded bg-[var(--bg)] p-3 text-sm">
        <div className="font-mono-num text-xs text-[var(--text-muted)]">
          {invoice.invoice_no}
        </div>
        <div className="mt-1">
          {t("finance.paid")}:{" "}
          <span className="font-mono-num font-semibold">
            {formatMoney(invoice.paid_paise)}
          </span>{" "}
          -{" "}
          <span className="font-mono-num font-semibold">
            {formatMoney(amountPaise)}
          </span>{" "}
          ={" "}
          <span className="font-mono-num font-semibold text-[var(--accent)]">
            {formatMoney(
              Math.max(0, Number(invoice.paid_paise || 0) - amountPaise),
            )}
          </span>
        </div>
        {cancelAfter && (
          <div className="mt-2 text-xs text-[var(--text-muted)]">
            {t("finance.refundAndCancelExplainer")}
          </div>
        )}
      </div>
      {refundableLines.length > 1 && (
        <div className="text-xs text-[var(--text-muted)]">
          {t("finance.refundByLineHint")}
        </div>
      )}
      {needsInvoiceLevel && (
        <div className="rounded bg-[var(--info-bg)] p-3 text-sm">
          <div className="flex items-center justify-between gap-3">
            <Label className="text-xs uppercase">
              {t("finance.invoiceLevelRefund")}
            </Label>
            {refundableLines.length > 0 && !cancelAfter && (
              <Switch
                checked={invoiceLevel}
                onCheckedChange={setInvoiceLevel}
                data-testid="invoice-level-refund-toggle"
              />
            )}
          </div>
          <div className="mt-1 text-xs text-[var(--text-muted)]">
            {t("finance.invoiceLevelRefundHint")}
          </div>
          {invoiceLevel && (
            <Input
              type="number"
              min="0.01"
              step="0.01"
              value={invoiceAmount}
              onChange={(e) => setInvoiceAmount(e.target.value)}
              className="mt-2 font-mono-num"
              placeholder="0.00"
              data-testid="invoice-refund-amount"
            />
          )}
        </div>
      )}
      {!invoiceLevel && refundableLines.map((l) => {
        const refundable =
          l.refundable_paise ?? (l.paid_paise || 0) - (l.refunded_paise || 0);
        return (
          <div key={l.id}>
            <Label className="text-xs uppercase">
              {l.fee_head_name || l.description || t("finance.lines")} -{" "}
              {t("finance.maxRefundable")}: {formatMoney(refundable)}
            </Label>
            <Input
              type="number"
              step="0.01"
              value={lineAmounts[l.id] ?? ""}
              onChange={(e) =>
                setLineAmounts((prev) => ({ ...prev, [l.id]: e.target.value }))
              }
              className="mt-1 font-mono-num"
              placeholder="0.00"
              data-testid={`invoice-refund-line-${l.id.slice(0, 8)}`}
            />
          </div>
        );
      })}
      {!invoiceLevel && blockedLines.map((l) => (
        <div
          key={l.id}
          className="rounded bg-[var(--bg)] p-2 text-xs text-[var(--text-muted)]"
        >
          {l.fee_head_name || l.description || t("finance.lines")}:{" "}
          {t("finance.notRefundable")}
        </div>
      ))}
      {cancelAfter && !fullRefundSelected && (
        <div className="rounded bg-[var(--warning-bg)] p-2 text-xs text-[var(--warning)]">
          {t("finance.fullRefundRequired")}
        </div>
      )}
      <div>
        <Label className="text-xs uppercase">{t("finance.method")}</Label>
        <Select value={method} onValueChange={(v) => setMethod(v)}>
          <SelectTrigger className="mt-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {methodOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs uppercase">{t("finance.reason")}</Label>
        <Input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="mt-1"
          placeholder={t("finance.refundReasonPlaceholder")}
          data-testid="invoice-refund-reason"
        />
      </div>
      <DialogFooter>
        <Button
          onClick={() => mut.mutate()}
          disabled={
            mut.isPending ||
            reason.trim().length < 3 ||
            amountPaise <= 0 ||
            amountPaise > Number(invoice.paid_paise || 0) ||
            (cancelAfter && !fullRefundSelected)
          }
          className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white"
          data-testid="submit-invoice-refund"
        >
          {mut.isPending ? t("finance.saving") : t(cancelAfter ? "finance.refundAndCancel" : "finance.recordRefund")}
        </Button>
      </DialogFooter>
    </div>
  );
}

function StructuresTab({ canWrite }) {
  const { t, language } = useI18n();
  const { user } = useAuth();
  const mode = levelSystemOf(user);
  const { data: structures, isLoading: structLoading } = useQuery({
    queryKey: ["fee-structures"],
    queryFn: async () => unwrap(await api.get("/fee-structures")),
  });
  const { data: heads, refetch: refetchHeads } = useQuery({
    queryKey: ["fee-heads"],
    queryFn: async () => unwrap(await api.get("/fee-heads")),
  });
  const { data: grades } = useQuery({
    queryKey: ["grades"],
    queryFn: async () => unwrap(await api.get("/grades")),
  });
  const [headDialogOpen, setHeadDialogOpen] = useState(false);
  const [editingHead, setEditingHead] = useState(null);
  const [structDialogOpen, setStructDialogOpen] = useState(false);
  const [editingStructure, setEditingStructure] = useState(null);
  const qc = useQueryClient();

  const toggleActiveMut = useMutation({
    mutationFn: async ({ structure, active }) =>
      unwrap(
        await api.patch(`/fee-structures/${structure.id}/active`, { active }),
      ),
    onSuccess: (data, { structure }) => {
      toast.success(
        data?.active !== false
          ? t("finance.structureActivated")
          : t("finance.structureDeactivated"),
      );
      qc.invalidateQueries({ queryKey: ["fee-structures"] });
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  return (
    <div className="mt-6 space-y-4">
      <Card className="border-[var(--subtle)]">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-sm font-display">
            {t("finance.feeHeads")}
          </CardTitle>
          {canWrite && (
            <Button
              size="sm"
              onClick={() => {
                setEditingHead(null);
                setHeadDialogOpen(true);
              }}
              className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white h-8"
              data-testid="add-fee-head-btn"
            >
              <Plus className="h-3.5 w-3.5 mr-1" /> {t("finance.addFeeHead")}
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm grid-table">
            <thead>
              <tr>
                <th className="text-left px-4 py-2.5">{t("finance.code")}</th>
                <th className="text-left px-4 py-2.5">{t("finance.name")}</th>
                <th className="text-left px-4 py-2.5">
                  {t("finance.recurring")}
                </th>
                <th className="text-left px-4 py-2.5">
                  {t("finance.optional")}
                </th>
                <th className="text-left px-4 py-2.5">{t("feePlan.custom")}</th>
                <th className="text-right px-4 py-2.5">{t("finance.gst")}</th>
                <th className="text-left px-4 py-2.5 w-16">
                  {t("finance.edit")}
                </th>
              </tr>
            </thead>
            <tbody>
              {(heads || []).map((h) => (
                <tr key={h.id} data-testid={`fee-head-${h.code}`}>
                  <td className="px-4 py-2.5 font-mono-num">{h.code}</td>
                  <td className="px-4 py-2.5">{h.name}</td>
                  <td className="px-4 py-2.5">
                    {h.is_recurring ? t("finance.yes") : t("finance.no")}
                  </td>
                  <td className="px-4 py-2.5">
                    {h.is_optional ? t("finance.yes") : t("finance.no")}
                  </td>
                  <td className="px-4 py-2.5" data-testid={`fee-head-custom-${h.code}`}>
                    {h.allow_custom ? t("finance.yes") : t("finance.no")}
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono-num">
                    {h.gst_rate_bps
                      ? `${(h.gst_rate_bps / 100).toFixed(1)}%`
                      : "—"}
                  </td>
                  <td className="px-4 py-2.5">
                    {canWrite && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setEditingHead(h);
                          setHeadDialogOpen(true);
                        }}
                        data-testid={`edit-fee-head-${h.code}`}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {(heads || []).length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="text-center py-6 text-[var(--text-muted)]"
                  >
                    {t("finance.noFeeHeads")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card className="border-[var(--subtle)]">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-sm font-display">
            {t("finance.publishedStructures")}
          </CardTitle>
          {canWrite && (
            <Button
              size="sm"
              onClick={() => {
                setEditingStructure(null);
                setStructDialogOpen(true);
              }}
              className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white h-8"
              data-testid="add-structure-btn"
            >
              <Plus className="h-3.5 w-3.5 mr-1" /> {t("finance.addStructure")}
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-0">
          {structLoading ? (
            <Skeleton className="h-32" />
          ) : (
            <table className="w-full text-sm grid-table">
              <thead>
                <tr>
                  <th className="text-left px-4 py-2.5">
                    {levelWord(mode, language)}
                  </th>
                  <th className="text-left px-4 py-2.5">{t("finance.name")}</th>
                  <th className="text-right px-4 py-2.5">
                    {t("finance.lines")}
                  </th>
                  <th className="text-right px-4 py-2.5">
                    {t("finance.total")}
                  </th>
                  <th className="text-left px-4 py-2.5">
                    {t("finance.status")}
                  </th>
                  <th className="text-left px-4 py-2.5 w-16">
                    {t("finance.edit")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {(structures || []).map((s) => {
                  const g =
                    s.grade_id === "all"
                      ? null
                      : (grades || []).find((g) => g.id === s.grade_id);
                  const gLabel =
                    s.grade_id === "all"
                      ? (mode === "age_band" ? t("finance.allAges") : t("finance.allGrades"))
                      : levelDisplayName(g, language, grades);
                  const total = (s.lines || []).reduce(
                    (a, l) => a + l.amount_paise,
                    0,
                  );
                  return (
                    <tr key={s.id} data-testid={`structure-${s.name}`}>
                      <td className="px-4 py-2.5">{gLabel}</td>
                      <td className="px-4 py-2.5">{s.name}</td>
                      <td className="px-4 py-2.5 text-right font-mono-num">
                        {(s.lines || []).length}
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono-num font-semibold">
                        {formatMoney(total)}
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2">
                          <Badge
                            className={
                              s.active !== false
                                ? "bg-[var(--success-bg)] text-[var(--success)] border-0"
                                : "bg-[var(--muted-bg)] text-[var(--text-muted)] border-0"
                            }
                          >
                            {s.active !== false
                              ? t("finance.active")
                              : t("finance.inactive")}
                          </Badge>
                          {canWrite && (
                            <Switch
                              checked={s.active !== false}
                              disabled={toggleActiveMut.isPending}
                              onCheckedChange={(checked) =>
                                toggleActiveMut.mutate({
                                  structure: s,
                                  active: checked,
                                })
                              }
                              data-testid={`toggle-structure-${s.name}`}
                            />
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-2.5">
                        {canWrite && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setEditingStructure(s);
                              setStructDialogOpen(true);
                            }}
                            data-testid={`edit-structure-${s.name}`}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {(structures || []).length === 0 && (
                  <tr>
                    <td
                      colSpan={6}
                      className="text-center py-6 text-[var(--text-muted)]"
                    >
                      {t("finance.noFeeStructures")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {canWrite && (
        <FeeHeadDialog
          open={headDialogOpen}
          onOpenChange={setHeadDialogOpen}
          editing={editingHead}
          onSuccess={() => {
            setHeadDialogOpen(false);
            refetchHeads();
          }}
        />
      )}
      {canWrite && (
        <FeeStructureDialog
          open={structDialogOpen}
          onOpenChange={setStructDialogOpen}
          editing={editingStructure}
          heads={heads || []}
          grades={grades || []}
          onSuccess={() => {
            setStructDialogOpen(false);
            setEditingStructure(null);
            qc.invalidateQueries({ queryKey: ["fee-structures"] });
          }}
        />
      )}
    </div>
  );
}

function FeeHeadDialog({ open, onOpenChange, editing, onSuccess }) {
  const { t } = useI18n();
  const blankForm = {
    name: "",
    code: "",
    is_recurring: true,
    is_optional: false,
    gst_rate_bps: 0,
    allow_custom: false,
    custom_min_pct: "",
    custom_max_pct: "",
    charge_when_bought: false,
  };
  const [form, setForm] = useState(blankForm);
  React.useEffect(() => {
    if (editing) {
      setForm({
        name: editing.name,
        code: editing.code,
        is_recurring: editing.is_recurring,
        is_optional: editing.is_optional || false,
        gst_rate_bps: editing.gst_rate_bps || 0,
        allow_custom: editing.allow_custom || false,
        custom_min_pct: editing.custom_min_bps == null ? "" : String(editing.custom_min_bps / 100),
        custom_max_pct: editing.custom_max_bps == null ? "" : String(editing.custom_max_bps / 100),
        charge_when_bought: editing.charge_when_bought || false,
      });
    } else {
      setForm(blankForm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, open]);

  // Limits are entered as a percentage of the standard amount and stored in
  // basis points; a blank field means no limit.
  const pctToBps = (value) => (value === "" ? null : Math.round(parseFloat(value) * 100));
  const limitsInvalid = form.allow_custom && (
    [form.custom_min_pct, form.custom_max_pct].some((value) => value !== "" && !(parseFloat(value) >= 0)) ||
    (form.custom_min_pct !== "" && form.custom_max_pct !== "" && parseFloat(form.custom_min_pct) > parseFloat(form.custom_max_pct))
  );
  const payload = () => {
    const { custom_min_pct, custom_max_pct, ...rest } = form;
    return {
      ...rest,
      custom_min_bps: form.allow_custom ? pctToBps(custom_min_pct) : null,
      custom_max_bps: form.allow_custom ? pctToBps(custom_max_pct) : null,
    };
  };

  const createMut = useMutation({
    mutationFn: async () => unwrap(await api.post("/fee-heads", payload())),
    onSuccess: () => {
      toast.success(t("finance.feeHeadCreated"));
      onSuccess();
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const updateMut = useMutation({
    mutationFn: async () =>
      unwrap(await api.put(`/fee-heads/${editing.id}`, payload())),
    onSuccess: () => {
      toast.success(t("finance.feeHeadUpdated"));
      onSuccess();
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-display">
            {editing ? t("finance.editFeeHead") : t("finance.createFeeHead")}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase">{t("finance.name")}</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-1"
              placeholder={t("finance.tuitionPlaceholder")}
              data-testid="fee-head-name"
            />
          </div>
          <div>
            <Label className="text-xs uppercase">{t("finance.code")}</Label>
            <Input
              value={form.code}
              onChange={(e) =>
                setForm({ ...form, code: e.target.value.toUpperCase() })
              }
              className="mt-1"
              placeholder={t("finance.codePlaceholder")}
              disabled={!!editing}
              data-testid="fee-head-code"
            />
          </div>
          <div>
            <Label className="text-xs uppercase">{t("finance.gstRate")}</Label>
            <Input
              type="number"
              value={form.gst_rate_bps}
              onChange={(e) =>
                setForm({
                  ...form,
                  gst_rate_bps: parseInt(e.target.value) || 0,
                })
              }
              className="mt-1"
              data-testid="fee-head-gst"
            />
            <div className="text-[10px] text-[var(--text-muted)] mt-1">
              {t("finance.gstHint")}
            </div>
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.is_recurring}
                onChange={(e) =>
                  setForm({ ...form, is_recurring: e.target.checked })
                }
                className="rounded"
              />
              {t("finance.recurring")}
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.is_optional}
                onChange={(e) =>
                  setForm({ ...form, is_optional: e.target.checked })
                }
                className="rounded"
              />
              {t("finance.optional")}
            </label>
          </div>
          {form.is_optional && (
            <div className="text-[10px] text-[var(--text-muted)]">
              {t("feePlan.optionalHint")}
            </div>
          )}
          <div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.charge_when_bought}
                onChange={(e) =>
                  setForm({ ...form, charge_when_bought: e.target.checked })
                }
                className="rounded"
                data-testid="fee-head-charge-when-bought"
              />
              {t("feePlan.chargeWhenBought")}
            </label>
            <div className="text-[10px] text-[var(--text-muted)] mt-1">
              {t("feePlan.chargeWhenBoughtHint")}
            </div>
          </div>
          <div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.allow_custom}
                onChange={(e) =>
                  setForm({ ...form, allow_custom: e.target.checked })
                }
                className="rounded"
                data-testid="fee-head-allow-custom"
              />
              {t("feePlan.allowCustom")}
            </label>
            <div className="text-[10px] text-[var(--text-muted)] mt-1">
              {t("feePlan.allowCustomHint")}
            </div>
          </div>
          {form.allow_custom && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase">{t("feePlan.customMin")}</Label>
                <Input
                  type="number"
                  min="0"
                  value={form.custom_min_pct}
                  placeholder={t("feePlan.noLimit")}
                  onChange={(e) => setForm({ ...form, custom_min_pct: e.target.value })}
                  className="mt-1"
                  data-testid="fee-head-custom-min"
                />
              </div>
              <div>
                <Label className="text-xs uppercase">{t("feePlan.customMax")}</Label>
                <Input
                  type="number"
                  min="0"
                  value={form.custom_max_pct}
                  placeholder={t("feePlan.noLimit")}
                  onChange={(e) => setForm({ ...form, custom_max_pct: e.target.value })}
                  className="mt-1"
                  data-testid="fee-head-custom-max"
                />
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            onClick={() => (editing ? updateMut.mutate() : createMut.mutate())}
            disabled={
              createMut.isPending ||
              updateMut.isPending ||
              limitsInvalid ||
              !form.name ||
              !form.code
            }
            className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white"
            data-testid="submit-fee-head"
          >
            {createMut.isPending || updateMut.isPending
              ? t("finance.saving")
              : editing
                ? t("finance.update")
                : t("finance.create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FeeStructureDialog({
  open,
  onOpenChange,
  heads,
  grades,
  onSuccess,
  editing,
}) {
  const { t, language } = useI18n();
  const { user } = useAuth();
  const mode = levelSystemOf(user);
  const feeFrequencyLookup = useLookupValues("fee_frequencies");
  const lookupFrequencyOptions = (feeFrequencyLookup.data?.values || []).map(
    (row) => ({
      value: row.value,
      label: row.label,
    }),
  );
  const frequencyOptions = lookupFrequencyOptions;
  const { data: years } = useQuery({
    queryKey: ["ay"],
    queryFn: async () => unwrap(await api.get("/academic-years")),
  });
  const currentYear =
    (years || []).find((y) => y.is_current) || (years || [])[0];
  const emptyLine = () => ({
    fee_head_id: "",
    amount_paise: "",
    frequency: "term",
    termAmounts: { term1: "", term2: "", term3: "" },
    installmentsPerTerm: "1",
  });
  const [form, setForm] = useState({
    academic_year_id: "",
    grade_id: "",
    name: "",
    lines: [emptyLine()],
  });
  React.useEffect(() => {
    if (editing) {
      setForm({
        academic_year_id: editing.academic_year_id || "",
        grade_id: editing.grade_id || "",
        name: editing.name || "",
        lines: (editing.lines || []).map((l) => ({
          fee_head_id: l.fee_head_id || "",
          amount_paise: l.amount_paise ? l.amount_paise / 100 : "",
          frequency: l.frequency || "term",
          period_label: l.period_label || null,
          termAmounts:
            l.termAmounts && l.frequency === "term"
              ? {
                  term1: l.termAmounts.term1
                    ? (l.termAmounts.term1 / 100).toString()
                    : "",
                  term2: l.termAmounts.term2
                    ? (l.termAmounts.term2 / 100).toString()
                    : "",
                  term3: l.termAmounts.term3
                    ? (l.termAmounts.term3 / 100).toString()
                    : "",
                }
              : { term1: "", term2: "", term3: "" },
          installmentsPerTerm:
            l.frequency === "term" && l.installmentsPerTerm
              ? String(l.installmentsPerTerm)
              : "1",
        })),
      });
    } else {
      setForm({
        academic_year_id: currentYear?.id || "",
        grade_id: "",
        name: "",
        lines: [emptyLine()],
      });
    }
  }, [editing, open, currentYear]);
  React.useEffect(() => {
    if (currentYear)
      setForm((f) => ({
        ...f,
        academic_year_id: f.academic_year_id || currentYear.id,
      }));
  }, [currentYear]);
  React.useEffect(() => {
    if (!frequencyOptions.length) return;
    setForm((prev) => {
      const fallback = frequencyOptions[0].value;
      let changed = false;
      const nextLines = prev.lines.map((line) => {
        if (
          frequencyOptions.some((option) => option.value === line.frequency)
        ) {
          return line;
        }
        changed = true;
        return { ...line, frequency: fallback };
      });
      return changed ? { ...prev, lines: nextLines } : prev;
    });
  }, [frequencyOptions]);

  const addLine = () =>
    setForm({
      ...form,
      lines: [...form.lines, emptyLine()],
    });
  const updateLine = (i, field, val) => {
    const lines = [...form.lines];
    lines[i] = { ...lines[i], [field]: val };
    setForm({ ...form, lines });
  };
  const updateTermAmount = (i, term, val) => {
    const lines = [...form.lines];
    lines[i] = {
      ...lines[i],
      termAmounts: { ...lines[i].termAmounts, [term]: val },
    };
    setForm({ ...form, lines });
  };
  const removeLine = (i) =>
    setForm({ ...form, lines: form.lines.filter((_, idx) => idx !== i) });

  const singleAmountPaise = (l) =>
    Math.round((parseFloat(l.amount_paise) || 0) * 100);
  const termTotalPaise = (l) =>
    (Math.round((parseFloat(l.termAmounts?.term1) || 0) * 100) || 0) +
    (Math.round((parseFloat(l.termAmounts?.term2) || 0) * 100) || 0) +
    (Math.round((parseFloat(l.termAmounts?.term3) || 0) * 100) || 0);
  const lineTotalPaise = (l) =>
    l.frequency === "term" ? termTotalPaise(l) : singleAmountPaise(l);

  const buildPayload = () => ({
    ...form,
    lines: form.lines.map((l) => {
      if (l.frequency === "term") {
        const termAmounts = {
          term1: Math.round((parseFloat(l.termAmounts?.term1) || 0) * 100),
          term2: Math.round((parseFloat(l.termAmounts?.term2) || 0) * 100),
          term3: Math.round((parseFloat(l.termAmounts?.term3) || 0) * 100),
        };
        return {
          fee_head_id: l.fee_head_id,
          frequency: l.frequency,
          period_label: l.period_label || null,
          termAmounts,
          installmentsPerTerm: parseInt(l.installmentsPerTerm, 10) || 1,
          amount_paise:
            termAmounts.term1 + termAmounts.term2 + termAmounts.term3,
        };
      }
      return {
        fee_head_id: l.fee_head_id,
        frequency: l.frequency,
        period_label: l.period_label || null,
        amount_paise: singleAmountPaise(l),
      };
    }),
  });

  const mut = useMutation({
    mutationFn: async () =>
      unwrap(await api.post("/fee-structures", buildPayload())),
    onSuccess: () => {
      toast.success(t("finance.structureCreated"));
      onSuccess();
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const updateMut = useMutation({
    mutationFn: async () =>
      unwrap(await api.put(`/fee-structures/${editing.id}`, buildPayload())),
    onSuccess: () => {
      toast.success(t("finance.structureUpdated"));
      onSuccess();
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display">
            {editing ? t("finance.editStructure") : t("finance.addStructure")}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
          <div>
            <Label className="text-xs uppercase">
              {t("finance.structureName")}
            </Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-1"
              placeholder={t("finance.structureName")}
              data-testid="structure-name"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase">{levelWord(mode, language)}</Label>
              <Select
                value={form.grade_id}
                onValueChange={(v) => setForm({ ...form, grade_id: v })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder={levelWord(mode, language)} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    {mode === "age_band" ? t("finance.allAges") : t("finance.allGrades")}
                  </SelectItem>
                  {grades.map((g) => (
                    <SelectItem key={g.id} value={g.id}>
                      {levelDisplayName(g, language, grades)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase">
                {t("finance.academicYear")}
              </Label>
              <Select
                value={form.academic_year_id}
                onValueChange={(v) => setForm({ ...form, academic_year_id: v })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(years || []).map((y) => (
                    <SelectItem key={y.id} value={y.id}>
                      {y.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs uppercase">
                {t("finance.lineItems")}
              </Label>
              <Button
                size="sm"
                variant="ghost"
                onClick={addLine}
                data-testid="add-line-btn"
              >
                <Plus className="h-3.5 w-3.5 mr-1" /> {t("finance.addLine")}
              </Button>
            </div>
            {form.lines.map((line, i) => {
              const isTerm = line.frequency === "term";
              return (
                <div
                  key={i}
                  className="mb-3 rounded-md border border-[var(--subtle)] p-2"
                >
                  <div className="flex items-center gap-2">
                    <Select
                      value={line.fee_head_id}
                      onValueChange={(v) => updateLine(i, "fee_head_id", v)}
                      className="flex-1"
                    >
                      <SelectTrigger>
                        <SelectValue
                          placeholder={t("finance.feeHeadPlaceholder")}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {heads.map((h) => (
                          <SelectItem key={h.id} value={h.id}>
                            {h.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select
                      value={line.frequency}
                      onValueChange={(v) => updateLine(i, "frequency", v)}
                      className="w-28"
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {frequencyOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {form.lines.length > 1 && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => removeLine(i)}
                        className="text-[var(--error)] h-8 px-2"
                      >
                        x
                      </Button>
                    )}
                  </div>
                  {isTerm ? (
                    <div className="mt-2">
                      <div className="mt-2 w-40">
                        <Label className="text-[10px] uppercase text-[var(--text-muted)]">
                          {t("finance.installmentsPerTerm")}
                        </Label>
                        <Select
                          value={line.installmentsPerTerm || "1"}
                          onValueChange={(v) =>
                            updateLine(i, "installmentsPerTerm", v)
                          }
                        >
                          <SelectTrigger
                            className="mt-0.5"
                            data-testid={`installments-per-term-${i}`}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {["1", "2"].map((n) => (
                              <SelectItem key={n} value={n}>
                                {n}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="grid grid-cols-3 gap-2 mt-2">
                        {["term1", "term2", "term3"].map((term) => (
                          <div key={term}>
                            <Label className="text-[10px] uppercase text-[var(--text-muted)]">
                              {t(`finance.${term}`)}
                            </Label>
                            <Input
                              type="number"
                              step="0.01"
                              value={line.termAmounts?.[term] || ""}
                              onChange={(e) =>
                                updateTermAmount(i, term, e.target.value)
                              }
                              className="mt-0.5 font-mono-num"
                              placeholder={t("finance.amount")}
                              data-testid={`term-${term}-${i}`}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="mt-2">
                      <Label className="text-[10px] uppercase text-[var(--text-muted)]">
                        {t("finance.amount")}
                      </Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={line.amount_paise || ""}
                        onChange={(e) =>
                          updateLine(i, "amount_paise", e.target.value)
                        }
                        className="mt-0.5 w-40 font-mono-num"
                        placeholder={t("finance.amount")}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="text-right font-mono-num text-sm font-semibold">
            {t("finance.total")}:{" "}
            {formatMoney(form.lines.reduce((a, l) => a + lineTotalPaise(l), 0))}
          </div>
        </div>
        <DialogFooter>
          <Button
            onClick={() => (editing ? updateMut.mutate() : mut.mutate())}
            disabled={
              mut.isPending ||
              updateMut.isPending ||
              !form.name ||
              !form.grade_id
            }
            className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white"
            data-testid="submit-structure"
          >
            {mut.isPending || updateMut.isPending
              ? t("finance.updating")
              : editing
                ? t("finance.update")
                : t("finance.addStructure")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DiscountsTab({ canWrite, canManageCustomDiscounts = false }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const { data: rules, refetch } = useQuery({
    queryKey: ["discount-rules"],
    queryFn: async () => unwrap(await api.get("/fees/discount-rules")),
  });
  const { data: students } = useQuery({
    queryKey: ["students-for-discount-eligibility"],
    queryFn: async () =>
      unwrap(await api.get("/students", { params: { limit: 500 } })),
    enabled: canWrite,
  });
  const [editOpen, setEditOpen] = useState(false);
  const [customDiscountForm, setCustomDiscountForm] = useState({ name: "", discount_type: "percentage", percentage: "", amount: "" });
  const customDiscountsQuery = useQuery({
    queryKey: ["custom-discounts"],
    queryFn: async () => unwrap(await api.get("/fees/custom-discounts")),
    enabled: canManageCustomDiscounts,
  });
  const customDiscountMut = useMutation({
    mutationFn: async () => unwrap(await api.post("/fees/custom-discounts", {
      name: customDiscountForm.name.trim(),
      discount_type: customDiscountForm.discount_type,
      ...(customDiscountForm.discount_type === "percentage"
        ? { percentage: Number(customDiscountForm.percentage) }
        : { amount_paise: Math.round(Number(customDiscountForm.amount) * 100) }),
    })),
    onSuccess: () => {
      setCustomDiscountForm({ name: "", discount_type: "percentage", percentage: "", amount: "" });
      customDiscountsQuery.refetch();
      queryClient.invalidateQueries({ queryKey: ["lookup-values", "fee_discount_types"] });
      toast.success(t("finance.customDiscountCreated"));
    },
    onError: (e) => toast.error(errMsg(e)),
  });
  const archiveCustomDiscountMut = useMutation({
    mutationFn: async (id) => unwrap(await api.patch(`/fees/custom-discounts/${id}/archive`)),
    onSuccess: () => {
      customDiscountsQuery.refetch();
      queryClient.invalidateQueries({ queryKey: ["lookup-values", "fee_discount_types"] });
      toast.success(t("finance.customDiscountArchived"));
    },
    onError: (e) => toast.error(errMsg(e)),
  });
  const [selectedStudentId, setSelectedStudentId] = useState("");
  const eligibilityQuery = useQuery({
    queryKey: ["discount-eligibility", selectedStudentId],
    queryFn: async () =>
      unwrap(
        await api.get(`/fees/discount-eligibilities/${selectedStudentId}`),
      ),
    enabled: canWrite && !!selectedStudentId,
  });
  const eligibilityMut = useMutation({
    mutationFn: async (twin_eligible) =>
      unwrap(
        await api.put(`/fees/discount-eligibilities/${selectedStudentId}`, {
          twin_eligible,
        }),
      ),
    onSuccess: () => {
      toast.success(t("finance.discountEligibilityUpdated"));
      eligibilityQuery.refetch();
    },
    onError: (e) => toast.error(errMsg(e)),
  });
  const percent = (bps) => `${((bps || 0) / 100).toFixed(2).replace(/\.00$/, "")} %`;
  const customDiscountValid = customDiscountForm.name.trim().length >= 2 && (
    customDiscountForm.discount_type === "percentage"
      ? customDiscountForm.percentage !== "" && Number(customDiscountForm.percentage) >= 0 && Number(customDiscountForm.percentage) <= 100
      : customDiscountForm.amount !== "" && Number.isFinite(Number(customDiscountForm.amount)) && Math.round(Number(customDiscountForm.amount) * 100) > 0
  );

  return (
    <div className="mt-6 space-y-4">
      {canManageCustomDiscounts && (
        <Card className="border-[var(--subtle)]">
          <CardHeader>
            <CardTitle className="text-sm font-display">{t("finance.customDiscounts")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid gap-2 sm:grid-cols-[1fr_9rem_8rem_auto]">
              <Input value={customDiscountForm.name} onChange={(event) => setCustomDiscountForm({ ...customDiscountForm, name: event.target.value })} placeholder={t("finance.customDiscountName")} data-testid="custom-discount-name" />
              <Select value={customDiscountForm.discount_type} onValueChange={(discount_type) => setCustomDiscountForm({ ...customDiscountForm, discount_type })}>
                <SelectTrigger data-testid="custom-discount-type"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="percentage">{t("finance.percentage")}</SelectItem><SelectItem value="amount">{t("finance.fixedAmount")}</SelectItem></SelectContent>
              </Select>
              {customDiscountForm.discount_type === "percentage" ? (
                <Input type="number" min="0" max="100" step="0.01" value={customDiscountForm.percentage} onChange={(event) => setCustomDiscountForm({ ...customDiscountForm, percentage: event.target.value })} placeholder="%" data-testid="custom-discount-percentage" />
              ) : (
                <Input type="number" min="0.01" step="0.01" value={customDiscountForm.amount} onChange={(event) => setCustomDiscountForm({ ...customDiscountForm, amount: event.target.value })} placeholder={t("finance.amount")} data-testid="custom-discount-amount" />
              )}
              <Button onClick={() => customDiscountMut.mutate()} disabled={customDiscountMut.isPending || !customDiscountValid} className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white" data-testid="create-custom-discount">
                <Plus className="h-4 w-4 mr-1" /> {t("finance.create")}
              </Button>
            </div>
            {(customDiscountsQuery.data || []).length > 0 && (
              <div className="divide-y divide-[var(--subtle)] rounded border border-[var(--subtle)]">
                {(customDiscountsQuery.data || []).map((discount) => (
                  <div key={discount.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                    <span>{discount.name}</span>
                    <span className="flex items-center gap-3">
                      <span className="font-mono-num">
                        {discount.discount_type === "amount" ? formatMoney(discount.amount_paise) : `${discount.percentage}%`}
                      </span>
                      {discount.active ? (
                        <Button size="sm" variant="ghost" onClick={() => archiveCustomDiscountMut.mutate(discount.id)} disabled={archiveCustomDiscountMut.isPending} data-testid={`archive-custom-discount-${discount.id}`}>
                          {t("finance.archive")}
                        </Button>
                      ) : <Badge variant="outline">{t("finance.archived")}</Badge>}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
      <Card className="border-[var(--subtle)]">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-sm font-display flex items-center gap-2">
            <Tag className="h-4 w-4 text-[var(--accent)]" />{" "}
            {t("finance.discountRules")}
          </CardTitle>
          {canWrite && (
            <Button
              size="sm"
              onClick={() => setEditOpen(true)}
              className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white h-8"
              data-testid="edit-sibling-rules"
            >
              <Pencil className="h-3.5 w-3.5 mr-1" /> {t("finance.editRules")}
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {rules ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div className="rounded bg-[var(--bg)] p-3">
                <div className="text-[10px] uppercase text-[var(--text-muted)]">
                  {t("finance.secondChildDiscount")}
                </div>
                <div className="font-mono-num text-2xl font-bold">
                  {percent(rules.second_child_bps)}
                </div>
              </div>
              <div className="rounded bg-[var(--bg)] p-3">
                <div className="text-[10px] uppercase text-[var(--text-muted)]">
                  {t("finance.twinDiscount")}
                </div>
                <div className="font-mono-num text-2xl font-bold">
                  {percent(rules.twin_bps)}
                </div>
              </div>
              <div className="rounded bg-[var(--bg)] p-3">
                <div className="text-[10px] uppercase text-[var(--text-muted)]">
                  {t("finance.scholarshipLevel1")}
                </div>
                <div className="font-mono-num text-2xl font-bold">
                  {percent(rules.scholarship_level_1_bps)}
                </div>
              </div>
              <div className="rounded bg-[var(--bg)] p-3">
                <div className="text-[10px] uppercase text-[var(--text-muted)]">
                  {t("finance.scholarshipLevel2")}
                </div>
                <div className="font-mono-num text-2xl font-bold">
                  {percent(rules.scholarship_level_2_bps)}
                </div>
              </div>
              <div className="sm:col-span-2 lg:col-span-4">
                <Badge
                  className={
                    rules.enabled !== false
                      ? "bg-[var(--success-bg)] text-[var(--success)]"
                      : "bg-[var(--error-bg)] text-[var(--error)]"
                  }
                >
                  {rules.enabled !== false
                    ? t("finance.enabled")
                    : t("finance.disabled")}
                </Badge>
              </div>
            </div>
          ) : (
            <div className="text-sm text-[var(--text-muted)]">
              {t("finance.noRulesConfigured")}
            </div>
          )}
        </CardContent>
      </Card>

      {canWrite && (
        <Card className="border-[var(--subtle)]">
          <CardHeader>
            <CardTitle className="text-sm font-display">
              {t("finance.twinEligibility")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Select
              value={selectedStudentId}
              onValueChange={setSelectedStudentId}
            >
              <SelectTrigger data-testid="discount-student-select">
                <SelectValue placeholder={t("finance.selectStudent")} />
              </SelectTrigger>
              <SelectContent>
                {(students || []).map((student) => (
                  <SelectItem key={student.id} value={student.id}>
                    {student.first_name} {student.last_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedStudentId && (
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={eligibilityQuery.data?.twin_eligible === true}
                  onChange={(event) =>
                    eligibilityMut.mutate(event.target.checked)
                  }
                  disabled={
                    eligibilityQuery.isLoading || eligibilityMut.isPending
                  }
                  className="rounded"
                  data-testid="twin-eligible-checkbox"
                />
                {t("finance.markTwinEligible")}
              </label>
            )}
          </CardContent>
        </Card>
      )}

      {canWrite && (
        <DiscountRuleDialog
          open={editOpen}
          onOpenChange={setEditOpen}
          rules={rules}
          onSuccess={() => {
            setEditOpen(false);
            refetch();
          }}
        />
      )}
    </div>
  );
}

function DiscountRuleDialog({ open, onOpenChange, rules, onSuccess }) {
  const { t } = useI18n();
  const [form, setForm] = useState({
    second_child_bps: 1000,
    twin_bps: 5000,
    scholarship_level_1_bps: 3333,
    scholarship_level_2_bps: 4722,
    enabled: true,
  });
  React.useEffect(() => {
    if (rules)
      setForm({
        second_child_bps: rules.second_child_bps ?? 1000,
        twin_bps: rules.twin_bps ?? 5000,
        scholarship_level_1_bps: rules.scholarship_level_1_bps ?? 3333,
        scholarship_level_2_bps: rules.scholarship_level_2_bps ?? 4722,
        enabled: rules.enabled !== false,
      });
  }, [rules, open]);

  const mut = useMutation({
    mutationFn: async () => unwrap(await api.put("/fees/discount-rules", form)),
    onSuccess: () => {
      toast.success(t("finance.rulesUpdated"));
      onSuccess();
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-display">
            {t("finance.discountRules")}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase">
              {t("finance.secondChildDiscount")} (%)
            </Label>
            <PercentInput
              value={form.second_child_bps}
              onChange={(value) =>
                setForm({ ...form, second_child_bps: value })
              }
              testId="second-child-pct"
            />
          </div>
          <div>
            <Label className="text-xs uppercase">
              {t("finance.twinDiscount")} (%)
            </Label>
            <PercentInput
              value={form.twin_bps}
              onChange={(value) => setForm({ ...form, twin_bps: value })}
              testId="twin-pct"
            />
          </div>
          <div>
            <Label className="text-xs uppercase">
              {t("finance.scholarshipLevel1")} (%)
            </Label>
            <PercentInput
              value={form.scholarship_level_1_bps}
              onChange={(value) =>
                setForm({ ...form, scholarship_level_1_bps: value })
              }
              testId="scholarship-level-1-pct"
            />
          </div>
          <div>
            <Label className="text-xs uppercase">
              {t("finance.scholarshipLevel2")} (%)
            </Label>
            <PercentInput
              value={form.scholarship_level_2_bps}
              onChange={(value) =>
                setForm({ ...form, scholarship_level_2_bps: value })
              }
              testId="scholarship-level-2-pct"
            />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.enabled}
              onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
              className="rounded"
            />
            {t("finance.enabled")}
          </label>
        </div>
        <DialogFooter>
          <Button
            onClick={() => mut.mutate()}
            disabled={mut.isPending}
            className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white"
            data-testid="submit-sibling-rules"
          >
            {mut.isPending ? t("finance.saving") : t("finance.saveRules")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PercentInput({ value, onChange, testId }) {
  return (
    <Input
      type="number"
      min="0"
      max="100"
      step="0.01"
      value={(value / 100).toFixed(2)}
      onChange={(event) =>
        onChange(Math.round((parseFloat(event.target.value) || 0) * 100))
      }
      className="mt-1 font-mono-num"
      data-testid={testId}
    />
  );
}

function DiscountApplyForm({ invoice, onSuccess }) {
  const { t } = useI18n();
  const discountLookup = useLookupValues("fee_discount_types");
  const [code, setCode] = useState("");
  const mut = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.post(`/fees/invoices/${invoice.id}/apply-discount`, {
          discount_code: code,
        }),
      ),
    onSuccess: () => {
      toast.success(t("finance.discountApplied"));
      onSuccess();
    },
    onError: (e) => toast.error(errMsg(e)),
  });
  const options = discountLookup.data?.values || [];
  return (
    <div className="space-y-3">
      <div className="rounded bg-[var(--bg)] p-3 text-sm">
        <div className="font-mono-num">{invoice.invoice_no}</div>
        <div>
          {invoice.student
            ? `${invoice.student.first_name} ${invoice.student.last_name}`
            : ""}
        </div>
      </div>
      <div>
        <Label className="text-xs uppercase">{t("finance.discountType")}</Label>
        <Select value={code} onValueChange={setCode}>
          <SelectTrigger className="mt-1" data-testid="discount-type">
            <SelectValue placeholder={t("finance.selectDiscountType")} />
          </SelectTrigger>
          <SelectContent>
            {options.map((row) => (
              <SelectItem key={row.value} value={row.value}>
                {t(`finance.discountTypes.${row.value}`) ===
                `finance.discountTypes.${row.value}`
                  ? row.label
                  : t(`finance.discountTypes.${row.value}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <DialogFooter>
        <Button
          onClick={() => mut.mutate()}
          disabled={!code || mut.isPending}
          className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white"
          data-testid="submit-discount"
        >
          {mut.isPending ? t("finance.saving") : t("finance.applyDiscount")}
        </Button>
      </DialogFooter>
    </div>
  );
}

function RefundsTab() {
  const { language, t } = useI18n();
  const methodLabels = {
    bank_transfer: "bankTransfer",
    card_reversal: "cardReversal",
    adjustment: "adjustment",
    cash: "cash",
  };
  const {
    data: refunds,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["refunds"],
    queryFn: async () => unwrap(await api.get("/fees/refunds")),
  });
  const { data: invoices } = useQuery({
    queryKey: ["invoices-paid"],
    queryFn: async () =>
      unwrap(await api.get("/invoices", { params: { limit: 200 } })),
  });
  const { data: me } = useQuery({
    queryKey: ["me"],
    queryFn: async () => unwrap(await api.get("/me")),
  });
  const [refundOpen, setRefundOpen] = useState(false);
  const qc = useQueryClient();
  const canWrite = hasCapability(me, CAPABILITY.FINANCE_MANAGE);

  const downloadReport = () => {
    const token = localStorage.getItem("eduos_token");
    if (!token) return;
    const url = `${process.env.REACT_APP_BACKEND_URL || ""}/api/v1/fees/refunds/report.xlsx?lang=${language}`;
    const xhr = new XMLHttpRequest();
    xhr.open("GET", url);
    xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    xhr.responseType = "blob";
    xhr.onload = () => {
      const blob = new Blob([xhr.response], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `refunds-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
    };
    xhr.send();
  };

  const paidInvoices = (invoices || []).filter((i) => i.paid_paise > 0);

  return (
    <div className="mt-6 space-y-4">
      <Card className="border-[var(--subtle)]">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-sm font-display flex items-center gap-2">
            <RefreshCcw className="h-4 w-4 text-[var(--accent)]" />{" "}
            {t("finance.refunds")}
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={downloadReport}
              data-testid="download-refund-report"
              className="h-8"
            >
              <Download className="h-3.5 w-3.5 mr-1" />{" "}
              {t("finance.downloadReport")}
            </Button>
            {canWrite && (
              <Button
                size="sm"
                onClick={() => setRefundOpen(true)}
                className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white h-8"
                data-testid="record-refund-btn"
              >
                <Plus className="h-3.5 w-3.5 mr-1" />{" "}
                {t("finance.recordRefund")}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <Skeleton className="h-48" />
          ) : (
            <table className="w-full text-sm grid-table">
              <thead>
                <tr>
                  <th className="text-left px-4 py-2.5">{t("finance.date")}</th>
                  <th className="text-left px-4 py-2.5">
                    {t("finance.student")}
                  </th>
                  <th className="text-right px-4 py-2.5">
                    {t("finance.amount")}
                  </th>
                  <th className="text-left px-4 py-2.5">
                    {t("finance.method")}
                  </th>
                  <th className="text-left px-4 py-2.5">
                    {t("finance.reason")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {(refunds || []).map((r) => (
                  <tr key={r.id} data-testid={`refund-${r.id.slice(0, 8)}`}>
                    <td className="px-4 py-2.5 font-mono-num text-xs">
                      {formatDate(r.processed_at)}
                    </td>
                    <td className="px-4 py-2.5">
                      {(r.student_name || r.student_id || "").slice(0, 20)}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono-num font-semibold">
                      {formatMoney(r.amount_paise)}
                    </td>
                    <td className="px-4 py-2.5 capitalize">
                      {t(`finance.${methodLabels[r.method] || r.method}`)}
                    </td>
                    <td className="px-4 py-2.5 text-[var(--text-muted)]">
                      {r.reason}
                    </td>
                  </tr>
                ))}
                {(refunds || []).length === 0 && (
                  <tr>
                    <td
                      colSpan={5}
                      className="text-center py-6 text-[var(--text-muted)]"
                    >
                      {t("finance.noRefunds")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <RefundDialog
        open={refundOpen}
        onOpenChange={setRefundOpen}
        invoices={paidInvoices}
        onSuccess={() => {
          setRefundOpen(false);
          refetch();
          qc.invalidateQueries();
        }}
      />
    </div>
  );
}

function RefundDialog({ open, onOpenChange, invoices, onSuccess }) {
  const { t } = useI18n();
  const refundMethodsLookup = useLookupValues("refund_methods");
  const lookupRefundOptions = (refundMethodsLookup.data?.values || []).map(
    (row) => ({ value: row.value, label: row.label }),
  );
  const refundMethodOptions = lookupRefundOptions;
  const [form, setForm] = useState({
    invoice_id: "",
    amount_paise: 0,
    reason: "",
    method: "bank_transfer",
  });
  const [lineAmounts, setLineAmounts] = useState({});
  const { data: refundDetail } = useQuery({
    queryKey: ["refund-invoice-detail", form.invoice_id],
    queryFn: async () => unwrap(await api.get(`/invoices/${form.invoice_id}`)),
    enabled: !!form.invoice_id,
  });
  const refundLines = (refundDetail?.lines || []).filter(
    (l) =>
      l.is_refundable &&
      (l.paid_paise || 0) - (l.refunded_paise || 0) > 0,
  );
  const mut = useMutation({
    mutationFn: async () => {
      const allocations = refundLines
        .filter(
          (l) => (Math.round(parseFloat(lineAmounts[l.id]) * 100) || 0) > 0,
        )
        .map((l) => ({
          invoice_line_id: l.id,
          amount_paise:
            Math.round(parseFloat(lineAmounts[l.id]) * 100) || 0,
        }));
      const payload = { ...form };
      if (
        allocations.length &&
        allocations.reduce((sum, item) => sum + item.amount_paise, 0) ===
          form.amount_paise
      ) {
        payload.line_allocations = allocations;
      }
      return unwrap(await api.post("/fees/refunds", payload));
    },
    onSuccess: () => {
      toast.success(t("finance.refundRecorded"));
      onSuccess();
    },
    onError: (e) => toast.error(errMsg(e)),
  });
  const selected = invoices.find((i) => i.id === form.invoice_id);
  const maxPaid = selected?.paid_paise || 0;

  React.useEffect(() => {
    if (!refundMethodOptions.length) return;
    if (refundMethodOptions.some((option) => option.value === form.method))
      return;
    setForm((prev) => ({ ...prev, method: refundMethodOptions[0].value }));
  }, [form.method, refundMethodOptions]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-display">
            {t("finance.recordRefund")}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase">{t("finance.invoice")}</Label>
            <Select
              value={form.invoice_id}
              onValueChange={(v) => {
                setForm({ ...form, invoice_id: v, amount_paise: 0 });
                setLineAmounts({});
              }}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder={t("finance.selectInvoice")} />
              </SelectTrigger>
              <SelectContent>
                {(invoices || []).map((i) => (
                  <SelectItem key={i.id} value={i.id}>
                    {i.invoice_no} — {formatMoney(i.paid_paise)}{" "}
                    {t("finance.paidSuffix")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="text-xs text-[var(--text-muted)]">
            {t("finance.maxRefundable")}:{" "}
            <span className="font-mono-num font-semibold">
              {formatMoney(maxPaid)}
            </span>
          </div>
          {refundLines.length > 1 && (
            <div className="text-xs text-[var(--text-muted)]">
              {t("finance.refundByLineHint")}
            </div>
          )}
          {refundLines.map((l) => {
            const refundable =
              (l.paid_paise || 0) - (l.refunded_paise || 0);
            return (
              <div key={l.id}>
                <Label className="text-xs uppercase">
                  {l.fee_head_name || l.description || t("finance.lines")} —{" "}
                  {t("finance.maxRefundable")}: {formatMoney(refundable)}
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  value={lineAmounts[l.id] ?? ""}
                  onChange={(e) => {
                    setLineAmounts((prev) => ({
                      ...prev,
                      [l.id]: e.target.value,
                    }));
                    const total = refundLines.reduce(
                      (sum, row) =>
                        sum +
                        (row.id === l.id
                          ? Math.round(parseFloat(e.target.value) * 100) || 0
                          : Math.round(parseFloat(prev[row.id]) * 100) || 0),
                      0,
                    );
                    setForm((prev) => ({ ...prev, amount_paise: total }));
                  }}
                  className="mt-1 font-mono-num"
                  placeholder="0.00"
                  data-testid={`refund-line-${l.id.slice(0, 8)}`}
                />
              </div>
            );
          })}
          <div>
            <Label className="text-xs uppercase">{t("finance.amount")}</Label>
            <Input
              type="number"
              value={form.amount_paise ? (form.amount_paise / 100).toFixed(2) : ""}
              onChange={(e) =>
                setForm({
                  ...form,
                  amount_paise:
                    Math.round(parseFloat(e.target.value) * 100) || 0,
                })
              }
              className="mt-1 font-mono-num"
              placeholder="0.00"
              data-testid="refund-amount"
            />
          </div>
          <div>
            <Label className="text-xs uppercase">{t("finance.method")}</Label>
            <Select
              value={form.method}
              onValueChange={(v) => setForm({ ...form, method: v })}
            >
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {refundMethodOptions.map((option) => {
                  const translationKey = `finance.${option.value}`;
                  const translated = t(translationKey);
                  const label =
                    translated === translationKey ? option.label : translated;
                  return (
                    <SelectItem key={option.value} value={option.value}>
                      {label}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase">{t("finance.reason")}</Label>
            <Input
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              className="mt-1"
              placeholder={t("finance.refundReasonPlaceholder")}
              data-testid="refund-reason"
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            onClick={() => mut.mutate()}
            disabled={
              mut.isPending ||
              !form.invoice_id ||
              !form.reason ||
              form.amount_paise <= 0 ||
              form.amount_paise > maxPaid
            }
            className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white"
            data-testid="submit-refund"
          >
            {mut.isPending ? t("finance.recording") : t("finance.recordRefund")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReportsTab({ canWrite = false }) {
  const today = new Date().toISOString().slice(0, 10);
  const [month, setMonth] = useState(today.slice(0, 7));
  const { t } = useI18n();
  const methodLabel = (method) => {
    const key = method === "bank_transfer" ? "bankTransfer" : method;
    return t(`finance.${key}`);
  };
  const { data: coll } = useQuery({
    queryKey: ["report-fee-collection", today],
    queryFn: async () =>
      unwrap(
        await api.get("/reports/fee-collection", { params: { date: today } }),
      ),
  });
  const { data: monthly } = useQuery({
    queryKey: ["report-fee-collection-monthly", month],
    queryFn: async () =>
      unwrap(
        await api.get("/reports/fee-collection-monthly", { params: { month } }),
      ),
  });
  const { data: overdue } = useQuery({
    queryKey: ["report-overdue"],
    queryFn: async () => unwrap(await api.get("/reports/overdue")),
  });
  const { data: out } = useQuery({
    queryKey: ["report-outstanding"],
    queryFn: async () => unwrap(await api.get("/reports/outstanding")),
  });

  return (
    <div className="mt-6 min-w-0 space-y-4">
      <TermBalanceReport canWrite={canWrite} />
      <CustomFeesReport />
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Card className="min-w-0 border-[var(--subtle)]">
          <CardHeader>
            <CardTitle className="text-sm font-display flex items-center gap-2">
              <Wallet className="h-4 w-4 text-[var(--accent)]" />{" "}
              {t("finance.collectionToday", { date: formatDate(today) })}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="min-w-0 rounded bg-[var(--bg)] p-3">
                <div className="text-[10px] uppercase text-[var(--text-muted)]">
                  {t("finance.today")}
                </div>
                <div className="font-mono-num text-xl sm:text-2xl font-bold truncate">
                  {formatMoney(coll?.day_total_paise || 0)}
                </div>
              </div>
              <div className="min-w-0 rounded bg-[var(--bg)] p-3">
                <div className="text-[10px] uppercase text-[var(--text-muted)]">
                  {t("finance.mtd")}
                </div>
                <div className="font-mono-num text-xl sm:text-2xl font-bold truncate">
                  {formatMoney(coll?.mtd_total_paise || 0)}
                </div>
              </div>
            </div>
            {coll && Object.keys(coll.method_breakdown || {}).length > 0 && (
              <div>
                <div className="text-xs uppercase text-[var(--text-muted)] font-semibold mb-2">
                  {t("finance.byMethodToday")}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[240px] text-sm">
                    <tbody>
                      {Object.entries(coll.method_breakdown || {}).map(
                        ([m, v]) => (
                          <tr
                            key={m}
                            className="border-b border-[var(--subtle)]"
                          >
                            <td className="py-1.5 capitalize">
                              {methodLabel(m)}
                            </td>
                            <td className="py-1.5 text-right font-mono-num">
                              {formatMoney(v)}
                            </td>
                          </tr>
                        ),
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="min-w-0 border-[var(--subtle)]">
          <CardHeader>
            <CardTitle className="text-sm font-display flex items-center gap-2">
              <Calendar className="h-4 w-4 text-[var(--accent)]" />{" "}
              {t("finance.monthlyCollection")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap items-center gap-3">
              <Input
                type="month"
                value={month}
                onChange={(e) => setMonth(e.target.value || today.slice(0, 7))}
                className="w-40 font-mono-num"
                data-testid="report-month"
              />
              <span className="text-xs text-[var(--text-muted)]">
                {t("finance.paymentCount", {
                  count: monthly?.payment_count || 0,
                })}
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="min-w-0 rounded bg-[var(--bg)] p-3">
                <div className="text-[10px] uppercase text-[var(--text-muted)]">
                  {t("finance.monthTotal")}
                </div>
                <div className="font-mono-num text-xl sm:text-2xl font-bold truncate">
                  {formatMoney(monthly?.month_total_paise || 0)}
                </div>
              </div>
            </div>
            {monthly &&
              Object.keys(monthly.method_breakdown || {}).length > 0 && (
                <div>
                  <div className="text-xs uppercase text-[var(--text-muted)] font-semibold mb-2">
                    {t("finance.byMethod")}
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[240px] text-sm">
                      <tbody>
                        {Object.entries(monthly.method_breakdown || {}).map(
                          ([m, v]) => (
                            <tr
                              key={m}
                              className="border-b border-[var(--subtle)]"
                            >
                              <td className="py-1.5 capitalize">
                                {methodLabel(m)}
                              </td>
                              <td className="py-1.5 text-right font-mono-num">
                                {formatMoney(v)}
                              </td>
                            </tr>
                          ),
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card className="min-w-0 border-[var(--subtle)]">
          <CardHeader>
            <CardTitle className="text-sm font-display flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-[var(--warning)]" />{" "}
              {t("finance.overdue")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="font-mono-num text-2xl sm:text-3xl font-bold text-[var(--warning)] truncate">
              {formatMoney(overdue?.total_overdue_paise || 0)}
            </div>
            <div className="text-xs text-[var(--text-muted)] mt-1">
              {t("finance.acrossPastDueInvoices", {
                count: overdue?.invoice_count || 0,
              })}
            </div>
          </CardContent>
        </Card>

        <Card className="min-w-0 border-[var(--subtle)]">
          <CardHeader>
            <CardTitle className="text-sm font-display">
              {t("finance.outstanding")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="font-mono-num text-2xl sm:text-3xl font-bold text-[var(--error)] truncate">
              {formatMoney(out?.total_outstanding_paise || 0)}
            </div>
            <div className="text-xs text-[var(--text-muted)] mt-1">
              {t("finance.acrossInvoices", { count: out?.invoice_count || 0 })}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
